import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Select, MenuItem, TextField, CircularProgress, Popper, ClickAwayListener } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { DropdownChevronIcon } from "./Primitives/icons/DropdownChevronIcon";
import DeleteIcon from "@material-ui/icons/Delete";
import { makeStyles } from "@material-ui/core/styles";
import { Editor } from "@tldraw/tldraw";
import cn from "classnames";
import { RowsIcon } from "@radix-ui/react-icons";
import Tooltip from "@material-ui/core/Tooltip";
import useTooltipStyles from "./style";
import { TextAlignCenterIcon, TextAlignLeftIcon, TextAlignRightIcon } from "./Primitives/icons";
import { getFontWeight, getFontWeightName } from "../../utils";
import { Title } from "./NewEffectSidebar";
import TextBoxComponent from "./TextBoxComponent";
import AddCustomFont from "./AddCustomFont";
import { s3CustomFontService } from "../../services/s3CustomFontService";
import { customFontStorage } from "../../services/customFontStorage";
import { LineHeightIcon } from "components/icons/components/LineHeightIcon";
import { LetterSpaceIcon } from "components/icons/components/LetterSpaceIcon";
import { useDragToAdjust } from "../hooks/useDragToAdjust";
import { AlignTopIcon } from "components/icons/components/AlignTopIcon";
import { AlignMiddleIcon } from "components/icons/components/AlignCenterVerticalIcon";
import { AlignBottomIcon } from "components/icons/components/AlignBottomIcon";
import {
  WEIGHT_ORDER,
  WEIGHT_PATTERNS,
  ITALIC_WEIGHT_PATTERNS,
  CONDENSED_WEIGHT_PATTERNS,
  RECOGNIZED_WEIGHTS,
  COMBINED_WEIGHT_ITALIC_PATTERNS,
  isItalicFontName,
  isCondensedFontName,
  isCondensedFontNameBasic,
  normalizeWeightName,
  getFontWeightValue,
  getFontFaceWeight,
  mapNumericWeightToName,
  mapStringWeightToName,
} from "./fontConstants";
import { useBrandKitStore } from "store/brandKit";
import BrandFontsQueueDialog, {
  BrandFontsQueueFamily,
} from "../../components/SideBar/Aside/components/BrandKit/BrandFontsQueueDialog";
import { SnackBarContext } from "components/snack-bar";
import { uploadBrandKitAsset } from "../../services/brandKitUploadService";
import { BrandKitFontPreset } from "graphQL/mutations/brandKit";
import "./styles.css";

const useStyles = makeStyles({
  inputRoot: {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      fontSize: "10px",
      color: "#00112B",
      height: "36px",

      "& fieldset": {
        borderColor: "#E1E6EB",
      },
      "&:hover fieldset": {
        borderColor: "#E1E6EB",
      },
    },
    "& .MuiAutocomplete-clearIndicator": {
      display: "none !important",
    },
    "& .MuiAutocomplete-popupIndicator": {
      marginRight: "2px",
    },
  },
  autocompleteOption: {
    "&:hover": {
      backgroundColor: "#E2E2FC !important",
    },
    fontSize: "12px",
  },
  selectRoot: {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      fontSize: "10px !important",
      color: "#00112B",
      cursor: "pointer",
      "& fieldset": {
        borderColor: "#E1E6EB",
      },
      "&:hover fieldset": {
        borderColor: "#E1E6EB",
      },
    },
    "& .MuiSelect-root": {
      fontSize: "10px !important",
      padding: 0,
      paddingLeft: 8,
      cursor: "pointer",
    },
    "& .MuiSelect-select": {
      cursor: "pointer",
      "&:focus": {
        backgroundColor: "transparent",
      },
    },
    "& .MuiSelect-icon": {
      cursor: "pointer",
      pointerEvents: "auto",
    },
    "& .MuiSelect-iconOpen": {
      cursor: "pointer",
      pointerEvents: "auto",
    },
  },
  listPadding: {
    width: "141px",
    padding: "0px !important",
  },

  paper: {
    borderRadius: "10px !important",
    padding: "0",
    display: "flex",
    justifyContent: "center",
    border: "1px solid #E1E6EB",
    boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.2)",
  },
  menuPaper: {
    marginTop: "25px",
    marginLeft: "-8px",
    borderRadius: "10px !important",
    "& .MuiMenuItem-root": {
      "&:hover": {
        backgroundColor: "#d3d3f8 !important",
      },
    },
  },
  textParametersRoot: {
    "& .MuiSelect-selectMenu": {
      padding: "0 !important",
      paddingLeft: "8px !important",
      backgroundColor: "white !important",
    },
    "& .MuiAutocomplete-input": {
      padding: "0 !important",
    },
    "& .MuiAutocomplete-popupIndicator": {
      padding: "2px",
      marginRight: "2px",
    },
    "& .MuiAutocomplete-option": {
      "&:hover": {
        backgroundColor: "#E2E2FC !important",
      },
    },
  },
  fontDropdownWrapper: {
    position: "relative" as any,
    width: "100%",
  },
  fontDropdownBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    height: 36,
    padding: "6px 8px",
    fontSize: 10,
    color: "#2E3860",
    background: "white",
    border: "1px solid #E2E2FC",
    borderRadius: 8,
    cursor: "pointer",
    "&:hover": { borderColor: "#B0B0E8" },
    "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
  },
  fontDropdownList: {
    position: "absolute" as any,
    top: "100%",
    left: 0,
    right: 0,
    marginTop: 2,
    background: "white",
    border: "1px solid #E2E2FC",
    borderRadius: 8,
    maxHeight: 280,
    overflowY: "auto" as any,
    zIndex: 9999,
    boxShadow: "0px 2px 8px rgba(0,0,0,0.08)",
    "&::-webkit-scrollbar": { width: 4 },
    "&::-webkit-scrollbar-thumb": { backgroundColor: "#C4C4D4", borderRadius: 4 },
    "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
  },
  fontDropdownSearch: {
    width: "100%",
    padding: "6px 8px",
    fontSize: 10,
    border: "1px solid #E2E2FC",
    borderRadius: 4,
    outline: "none",
    boxSizing: "border-box" as any,
    "&:focus": { borderColor: "#B0B0E8" },
    "&::placeholder": { color: "#A0A0B8" },
  },
  fontDropdownItem: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    padding: "6px 8px",
    fontSize: 11,
    color: "#2E3860",
    background: "transparent",
    border: "none",
    textAlign: "left" as any,
    cursor: "pointer",
    "&:hover": { background: "#E2E2FC" },
  },
});

// Helper function to detect font style (italic) from font file metadata
// Reads the OS/2 table from TTF/OTF files to check fsSelection bit 0 (italic)
async function detectFontStyleFromFile(file: File, fontName: string): Promise<string | null> {
  try {
    // Read file as ArrayBuffer to parse TTF/OTF metadata
    const arrayBuffer = await file.arrayBuffer();
    const view = new DataView(arrayBuffer);

    // TTF/OTF files start with a header (offset table)
    // Check if it's a valid TTF/OTF file
    const sfntVersion = view.getUint32(0, false); // Big-endian
    const isTTF = sfntVersion === 0x00010000 || sfntVersion === 0x74727565; // 'true' for OTF
    const isOTF = sfntVersion === 0x4f54544f; // 'OTTO' for CFF-based OTF

    if (!isTTF && !isOTF) {
      return null;
    }

    // Read number of tables
    const numTables = view.getUint16(4, false);

    // Find OS/2 table (contains style information)
    let os2Offset = 0;
    const tableDirectoryOffset = 12;

    for (let i = 0; i < numTables; i++) {
      const tableOffset = tableDirectoryOffset + i * 16;
      const tag = String.fromCharCode(
        view.getUint8(tableOffset),
        view.getUint8(tableOffset + 1),
        view.getUint8(tableOffset + 2),
        view.getUint8(tableOffset + 3)
      );

      if (tag === "OS/2") {
        os2Offset = view.getUint32(tableOffset + 8, false);
        break;
      }
    }

    if (os2Offset === 0) {
      return null;
    }

    // Read fsSelection from OS/2 table (offset 62 from start of OS/2 table)
    // fsSelection is a uint16 at offset 62
    // Bit 0 (0x0001) indicates italic
    const fsSelection = view.getUint16(os2Offset + 62, false);
    const isItalic = (fsSelection & 0x0001) !== 0;

    return isItalic ? "italic" : "normal";
  } catch (error) {
    console.warn(`⚠️ Error detecting font style from file "${fontName}":`, error);
    return null;
  }
}

// Helper function to detect font weight from font file metadata
// Reads the OS/2 table from TTF/OTF files to get usWeightClass
async function detectFontWeightFromFile(file: File, fontName: string): Promise<string | null> {
  try {
    // Read file as ArrayBuffer to parse TTF/OTF metadata
    const arrayBuffer = await file.arrayBuffer();
    const view = new DataView(arrayBuffer);

    // TTF/OTF files start with a header (offset table)
    // Check if it's a valid TTF/OTF file
    const sfntVersion = view.getUint32(0, false); // Big-endian
    const isTTF = sfntVersion === 0x00010000 || sfntVersion === 0x74727565; // 'true' for OTF
    const isOTF = sfntVersion === 0x4f54544f; // 'OTTO' for CFF-based OTF

    if (!isTTF && !isOTF) {
      console.warn(`⚠️ File "${fontName}" doesn't appear to be a valid TTF/OTF file`);
      return null;
    }

    // Read number of tables
    const numTables = view.getUint16(4, false);

    // Find OS/2 table (contains weight information)
    let os2Offset = 0;
    let os2Length = 0;
    const tableDirectoryOffset = 12;

    for (let i = 0; i < numTables; i++) {
      const tableOffset = tableDirectoryOffset + i * 16;
      const tag = String.fromCharCode(
        view.getUint8(tableOffset),
        view.getUint8(tableOffset + 1),
        view.getUint8(tableOffset + 2),
        view.getUint8(tableOffset + 3)
      );

      if (tag === "OS/2") {
        os2Offset = view.getUint32(tableOffset + 8, false);
        os2Length = view.getUint32(tableOffset + 12, false);
        break;
      }
    }

    if (os2Offset === 0) {
      console.warn(`⚠️ OS/2 table not found in font "${fontName}"`);
      return null;
    }

    // Read usWeightClass from OS/2 table (offset 4 from start of OS/2 table)
    // usWeightClass is a uint16 at offset 4
    const usWeightClass = view.getUint16(os2Offset + 4, false);

    // Map usWeightClass to weight name using centralized function
    return mapNumericWeightToName(usWeightClass);
  } catch (error) {
    console.warn(`⚠️ Error detecting font weight from file "${fontName}":`, error);
    // Fallback: try FontFace API
    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error("Failed to read file"));
        reader.readAsDataURL(file);
      });

      const tempFontFace = new FontFace(`temp_${fontName}_${Date.now()}`, `url(${dataUrl})`);
      await tempFontFace.load();

      const weight = tempFontFace.weight;

      if (typeof weight === "string") {
        return mapStringWeightToName(weight);
      } else if (typeof weight === "number") {
        return mapNumericWeightToName(weight);
      }
    } catch (fallbackError) {
      console.warn(`⚠️ FontFace fallback also failed:`, fallbackError);
    }

    return null;
  }
}

// Helper function to extract weight from condensed font name
// Examples: "AdobeClean-BoldSemiCn" -> "Bold", "FontName-BlackCondensed" -> "Black", "FontName-BoldExtraCondensed" -> "Bold"
function extractWeightFromCondensedFontName(fontName: string): string | null {
  const fontNameLower = fontName.toLowerCase();

  // Use centralized condensed weight patterns
  for (const pattern of CONDENSED_WEIGHT_PATTERNS) {
    const match = fontName.match(pattern);
    if (match) {
      const weight = match[1];
      // Use centralized normalization function
      return normalizeWeightName(weight);
    }
  }

  return null;
}

// Helper function to extract weight from italic font name
// Examples: "PlayfairDisplay-BlackItalic" -> "Black", "FontName-BoldItalic" -> "Bold"
function extractWeightFromItalicFontName(fontName: string): string | null {
  const fontNameLower = fontName.toLowerCase();

  // Use centralized italic weight patterns
  for (const pattern of ITALIC_WEIGHT_PATTERNS) {
    const match = fontName.match(pattern);
    if (match) {
      const weight = match[1];
      // Use centralized normalization function
      return normalizeWeightName(weight);
    }
  }

  return null;
}

// Helper function to extract weight from font name
// Examples: "UberMove-Medium" -> "Medium", "UberMoveBold" -> "Bold", "Uber Text-Regular" -> "Regular", "UberMove-re" -> "Regular"
function extractWeightFromFontName(fontName: string): string | null {
  // First check if it's an italic font and extract weight from that
  const fontNameLower = fontName.toLowerCase();
  if (fontNameLower.includes("italic") || fontNameLower.includes("it")) {
    const italicWeight = extractWeightFromItalicFontName(fontName);
    if (italicWeight) return italicWeight;
  }
  // Use centralized weight patterns
  for (const pattern of WEIGHT_PATTERNS) {
    const match = fontName.match(pattern);
    if (match) {
      const weight = match[1] || match[0];
      // Remove any trailing numbers or spaces
      const cleaned = weight.replace(/\s*\d+$/, "").trim();
      // Use centralized normalization function
      return normalizeWeightName(cleaned);
    }
  }

  return null;
}

// Helper function to normalize font names with multiple hyphens before weight/style
// Examples: "SF-Pro-Display-Regular" -> "SFProDisplay-Regular", "Adobe-Clean-Bold" -> "AdobeClean-Bold"
function normalizeFontName(fontName: string): string {
  // Split by hyphens
  const parts = fontName.split("-");

  // If there are less than 2 parts, no need to normalize
  if (parts.length < 2) {
    return fontName;
  }

  // Check if the last part (or combination of last parts) is a recognized weight/style
  let weightStyleIndex = -1;
  const lastPart = parts[parts.length - 1]
    .toLowerCase()
    .replace(/\s*\d+$/, "")
    .trim();
  const secondToLastPart =
    parts.length > 2
      ? parts[parts.length - 2]
          .toLowerCase()
          .replace(/\s*\d+$/, "")
          .trim()
      : "";

  // Check for combined weight+style patterns like "BoldItalic", "HeavyItalic"
  const combinedPattern =
    parts.length > 1
      ? (parts[parts.length - 2] + parts[parts.length - 1])
          .toLowerCase()
          .replace(/\s*\d+$/, "")
          .trim()
      : "";

  // Use centralized recognized weights and combined patterns
  const recognizedWeights: string[] = [
    ...RECOGNIZED_WEIGHTS,
    "italic",
    "it",
    "condensed",
    "cond",
    "cn",
    "semicn",
    "semicond",
  ];
  const combinedWeights: string[] = [...COMBINED_WEIGHT_ITALIC_PATTERNS];

  // First check if lastPart itself is a combined pattern (e.g., "BoldItalic" -> "bolditalic")
  if (combinedWeights.includes(lastPart)) {
    // The last part is already a combined weight+style pattern
    weightStyleIndex = parts.length - 1;
  } else if (combinedPattern && combinedWeights.includes(combinedPattern)) {
    // If it's a combined pattern spanning two parts (e.g., "Display-BoldItalic" -> "displaybolditalic")
    // This is less common, but handle it
    weightStyleIndex = parts.length - 2;
  } else if (recognizedWeights.includes(lastPart)) {
    weightStyleIndex = parts.length - 1;
  } else if (parts.length > 2 && recognizedWeights.includes(secondToLastPart)) {
    // Check if second to last is weight and last is a number (e.g., "Regular 2")
    weightStyleIndex = parts.length - 2;
  }

  // If we found a weight/style, join all parts before it without hyphens
  if (weightStyleIndex > 0) {
    const fontParts = parts.slice(0, weightStyleIndex);
    const weightStyleParts = parts.slice(weightStyleIndex);
    const normalizedFontName = fontParts.join("") + "-" + weightStyleParts.join("-");
    return normalizedFontName;
  }

  // If no recognized weight/style found, return original
  return fontName;
}

// Helper function to extract base font family name
// Examples: "UberMove-Medium" -> "UberMove", "Uber Text-Regular" -> "UberText",
// "UberMoveBold" -> "UberMove", "UberMove-Medium 2" -> "UberMove",
// "UberMove-re" -> "UberMove", "FortalesiaPlain_PERSONAL_USE_ONLY" -> "FortalesiaPlain",
// "FontName-BoldItalic" -> "FontName"
// Spaces in the base name are stripped so "UberMove Text" and "UberMoveText"
// collapse to the same family.
function extractBaseFontName(fontName: string): string {
  let baseName = fontName;

  // First, remove italic variations (including BoldItalic, RegularItalic, etc.)
  // This must be done before removing weights to handle combined names like "BoldItalic"
  // IMPORTANT: Order matters! extrabold/extra-bold BEFORE extralight/extra-light
  const italicPatterns = [
    /-?(bold|regular|medium|light|thin|semibold|semi-bold|extrabold|extra-bold|extralight|extra-light|black|heavy|ultrabold|ultra-bold)?italic\s*\d*$/i,
    /-?(bold|regular|medium|light|thin|semibold|semi-bold|extrabold|extra-bold|extralight|extra-light|black|heavy|ultrabold|ultra-bold)?it\s*\d*$/i,
  ];

  for (const pattern of italicPatterns) {
    baseName = baseName.replace(pattern, "").trim();
  }

  // Then, try to detect if there's a recognized weight suffix
  const recognizedWeight = extractWeightFromFontName(baseName);

  // If we found a recognized weight, remove it
  if (recognizedWeight) {
    // Remove weight suffixes with optional trailing numbers and spaces
    // IMPORTANT: Order matters! Longer patterns (extrabold, extra-bold, extra-light) must come before shorter ones
    const weightPatterns = [
      // Full names with hyphen (extrabold/extra-bold BEFORE extralight/extra-light to avoid partial matches)
      /-(bold|semibold|semi-bold|medium|regular|reg|normal|light|thin|extrabold|extra-bold|extralight|extra-light|black|heavy|ultrabold|ultra-bold)\s*\d*$/i,
      // Abbreviations with hyphen: -re, -bd, -md, -lt, -sb, -eb, etc. (eb/extrabold BEFORE el/extralight)
      /-(re|reg|bd|bld|md|lt|sb|th|eb|extrabold|el|extralight|bk|black|hv|heavy|ub|ultrabold)\s*\d*$/i,
      // Full names without hyphen (extrabold/extra-bold BEFORE extralight/extra-light)
      /(bold|semibold|semi-bold|medium|regular|reg|normal|light|thin|extrabold|extra-bold|extralight|extra-light|black|heavy|ultrabold|ultra-bold)\s*\d*$/i,
      // Abbreviations without hyphen: re, bd, md, lt, sb, eb, etc. (eb/extrabold BEFORE el/extralight)
      /(re|reg|bd|bld|md|lt|sb|th|eb|extrabold|el|extralight|bk|black|hv|heavy|ub|ultrabold)\s*\d*$/i,
      // Also handle patterns with space before number: "Medium 2", "Regular 2", etc. (extrabold BEFORE extralight)
      /\s*-?\s*(bold|semibold|semi-bold|medium|regular|reg|normal|light|thin|extrabold|extra-bold|extralight|extra-light|black|heavy|ultrabold|ultra-bold)\s+\d+$/i,
    ];

    for (const pattern of weightPatterns) {
      baseName = baseName.replace(pattern, "").trim();
    }
    return normalizeBaseName(baseName || fontName);
  }

  // If no recognized weight, but there's a separator (_ or -), cut from there
  // This handles cases like "FortalesiaPlain_PERSONAL_USE_ONLY" -> "FortalesiaPlain"
  const separatorMatch = baseName.match(/^(.+?)[_-]/);
  if (separatorMatch) {
    return normalizeBaseName(separatorMatch[1]);
  }

  return normalizeBaseName(baseName || fontName);
}

/**
 * Strip internal whitespace from a base family name so variants named
 * "UberMove Text" and "UberMoveText" collapse into a single family.
 */
function normalizeBaseName(name: string): string {
  return (name || "").replace(/\s+/g, "").trim();
}

// normalizeWeightName and getFontWeightValue are now imported from fontConstants.ts

interface TextParametersProps {
  selectedShape: any;
  selectedFont: any;
  setSelectedFont: (font: string) => void;
  selectedShapes: any[];
  editor: Editor;
  setSelectedShape: (shape: any) => void;
  fonts: any;
}

const TextParameters = ({
  selectedShape,
  setSelectedFont,
  editor,
  setSelectedShape,
  fonts,
  selectedShapes,
}: TextParametersProps) => {
  const classes = useStyles();
  const classesTooltip = useTooltipStyles();
  const runSnackBar = React.useContext(SnackBarContext);

  const [fontSize, setFontSize] = React.useState(selectedShape ? selectedShape.props.size : "");
  const [fontScale, setFontScale] = React.useState(selectedShape ? selectedShape.props.scale : 1);
  const [displayFontSize, setDisplayFontSize] = React.useState("");
  const [fontSizeOpen, setFontSizeOpen] = React.useState(false);
  const [fontSelectorOpen, setFontSelectorOpen] = React.useState(false);
  const [fontsLoading, setFontsLoading] = React.useState(true);
  const [fontLoadError, setFontLoadError] = React.useState(false);
  const [customFontsMap, setCustomFontsMap] = useState<Map<string, Set<string>>>(new Map());
  // Map: fontName -> detected weight (from metadata or name)
  const [fontNameToWeightMap, setFontNameToWeightMap] = useState<Map<string, string>>(new Map());
  // Track which font is in delete confirmation mode
  const [fontToDelete, setFontToDelete] = useState<string | null>(null);
  // Track which font is currently being deleted
  const [deletingFont, setDeletingFont] = useState<string | null>(null);
  // Snapshot to support hover preview and restoration when closing menus without selection.
  // `byShape` holds the original props of EACH selected shape so that multi-selection
  // restores per-shape (preventing one shape's font from being applied to all when
  // the hover is cancelled). The top-level font/fontWeight/italic fields are a
  // representative snapshot (from the primary selected shape) kept for existing
  // read-back paths (e.g. weight detection during hover).
  const [previewSnapshot, setPreviewSnapshot] = useState<{
    font?: string;
    fontWeight?: any;
    italic?: string;
    byShape?: Array<{ id: string; type: string; font?: string; fontWeight?: any; italic?: string }>;
  } | null>(null);
  // Tracks if a selection was committed while dropdown is open
  const selectionCommittedRef = useRef(false);
  const [weightMenuAnchor, setWeightMenuAnchor] = useState<HTMLElement | null>(null);
  const weightMenuOpen = Boolean(weightMenuAnchor);
  // Tracks hovered menu item for visual highlight
  const [hoveredMenuItem, setHoveredMenuItem] = useState<string | null>(null);
  // Popper container ref for outside-click detection
  const popperRef = useRef<HTMLDivElement | null>(null);
  // Track if font dropdown is open to handle preview restoration
  const [fontDropdownOpen, setFontDropdownOpen] = useState<boolean>(false);
  const prevFontDropdownOpenRef = useRef<boolean>(false);
  // Ref to store the current snapshot without causing re-renders
  const previewSnapshotRef = useRef<{
    font?: string;
    fontWeight?: any;
    italic?: string;
    byShape?: Array<{ id: string; type: string; font?: string; fontWeight?: any; italic?: string }>;
  } | null>(null);
  // Font upload loading state
  const [fontUploading, setFontUploading] = useState(false);
  // Custom font dropdown state (Brand Kit-style)
  const [fontSearchTerm, setFontSearchTerm] = useState("");
  const fontSearchInputRef = useRef<HTMLInputElement | null>(null);
  const fontDropdownWrapperRef = useRef<HTMLDivElement | null>(null);

  // Brand font presets
  const { fontPresets, fonts: brandKitFonts, loadBrandKit, addFontPreset } = useBrandKitStore();

  // After uploading a font, optionally set it as a brand font preset.
  // Queue of detected font families — each row can be added or skipped.
  const [brandFontQueueOpen, setBrandFontQueueOpen] = useState(false);
  const [brandFontQueue, setBrandFontQueue] = useState<BrandFontsQueueFamily[]>([]);
  const brandFontAnchorRef = useRef<HTMLLabelElement | null>(null);

  // Set of font base names that belong to the Brand Kit (uploaded fonts + preset families)
  const brandFontNames = useMemo(() => {
    const names = new Set<string>();
    // Font families used in brand font presets
    fontPresets.forEach((p: BrandKitFontPreset) => {
      names.add(extractBaseFontName(p.fontFamily));
    });
    // Fonts uploaded to the Brand Kit fonts section
    if (brandKitFonts?.items) {
      brandKitFonts.items.forEach((item: any) => {
        if (item.name) names.add(extractBaseFontName(item.name));
      });
    }
    return names;
  }, [fontPresets, brandKitFonts]);

  // Map font family base names to their Brand Kit preset labels for display
  const brandPresetLabelMap = useMemo(() => {
    const map = new Map<string, string>();
    fontPresets.forEach((p: BrandKitFontPreset) => {
      const base = extractBaseFontName(p.fontFamily);
      if (!map.has(base)) {
        map.set(base, p.label || p.fontFamily);
      }
    });
    return map;
  }, [fontPresets]);

  const [presetDropdownOpen, setPresetDropdownOpen] = useState(false);
  const [selectedPresetId, setSelectedPresetId] = useState<string | null>(null);
  const presetDropdownRef = useRef<HTMLDivElement>(null);

  // Helper function to show notifications
  const showNotification = useCallback((message: string, type: "success" | "error" | "info" = "info") => {
    try {
      // Remove existing notification if any
      const existing = window.document.getElementById("font-upload-status");
      if (existing) {
        existing.remove();
      }

      const colors = {
        success: "#4caf50",
        error: "#f44336",
        info: "#2196f3",
      };

      const notificationDiv = window.document.createElement("div");
      notificationDiv.id = "font-upload-status";
      notificationDiv.innerHTML = message;
      notificationDiv.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: ${colors[type]};
        color: white;
        padding: 12px 16px;
        border-radius: 4px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.2);
        z-index: 10000;
        font-size: 14px;
        font-weight: 500;
        animation: slideIn 0.3s ease-out;
      `;

      // Add animation keyframes if not exists
      if (!window.document.querySelector("#font-notification-animation")) {
        const style = window.document.createElement("style");
        style.id = "font-notification-animation";
        style.textContent = `
          @keyframes slideIn {
            from { transform: translateX(100%); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }
          @keyframes slideOut {
            from { transform: translateX(0); opacity: 1; }
            to { transform: translateX(100%); opacity: 0; }
          }
        `;
        window.document.head.appendChild(style);
      }

      window.document.body.appendChild(notificationDiv);

      // Auto-remove success/error notifications after 3 seconds
      if (type === "success" || type === "error") {
        setTimeout(() => {
          notificationDiv.style.animation = "slideOut 0.3s ease-in";
          setTimeout(() => {
            if (notificationDiv.parentNode) {
              notificationDiv.parentNode.removeChild(notificationDiv);
            }
          }, 300);
        }, 3000);
      }
    } catch (notificationError) {
      console.log("Could not show notification:", notificationError);
      // Fallback: Use alert
      alert(message);
    }
  }, []);

  // Memoized arrays to prevent unnecessary re-renders
  const textAlignment = useMemo(
    () => [
      { value: "start", label: "Left align", icon: <TextAlignLeftIcon width={12} height={12} /> },
      { value: "middle", label: "Center align", icon: <TextAlignCenterIcon width={12} height={12} /> },
      { value: "end", label: "Right align", icon: <TextAlignRightIcon width={12} height={12} /> },
    ],
    []
  );

  const verticalAlignment = useMemo(
    () => [
      { value: "start", label: "Align top", icon: <AlignTopIcon className="w-4 h-4" /> },
      { value: "middle", label: "Align middle", icon: <AlignMiddleIcon /> },
      { value: "end", label: "Align bottom", icon: <AlignBottomIcon /> },
    ],
    []
  );

  // Returns the real font name (used for logic: available styles, weight detection, etc.)
  const getFontDisplayValue = useCallback(() => {
    if (!selectedShapes || selectedShapes.length === 0) return "";

    const textShapes = selectedShapes.filter((shape) => shape?.props?.text);
    if (textShapes.length === 0) return "";

    // If there is an active preview snapshot, ALWAYS use the original value from the snapshot
    // This prevents showing the last hovered font at any time
    if (previewSnapshot && previewSnapshot.font) {
      const snapshotBaseName = extractBaseFontName(previewSnapshot.font);
      return snapshotBaseName;
    }

    const firstFont = textShapes[0]?.props?.font;
    const isMixed = textShapes.some((shape) => shape?.props?.font !== firstFont);

    if (isMixed) return "Mixed";

    const fontName = firstFont?.toString() || "";
    if (!fontName) return "";

    // For custom fonts, return the base name instead of the full name
    const baseName = extractBaseFontName(fontName);
    if (customFontsMap.has(baseName)) {
      return baseName;
    }

    return fontName;
  }, [selectedShapes, customFontsMap, previewSnapshot]);

  // Returns the display name for the dropdown button (shows Brand Kit label if applicable)
  const getFontButtonLabel = useCallback(() => {
    const realValue = getFontDisplayValue();
    if (!realValue || realValue === "Mixed") return realValue;
    const baseName = extractBaseFontName(realValue);
    if (brandPresetLabelMap.has(baseName)) {
      return brandPresetLabelMap.get(baseName)!;
    }
    return realValue;
  }, [getFontDisplayValue, brandPresetLabelMap]);

  // Helper function to check if a font is custom
  const isCustomFont = useCallback(
    (fontName: string | null | undefined): boolean => {
      if (!fontName) return false;

      // Check if font name exists in custom fonts map (by base name)
      const baseName = extractBaseFontName(fontName);
      return customFontsMap.has(baseName);
    },
    [customFontsMap]
  );

  // Get available weights for the selected font
  const getAvailableWeights = useCallback(
    (fontName: string | null | undefined): string[] => {
      if (!fontName) return ["Bold", "Semi-Bold", "Normal", "Light"];

      const baseName = extractBaseFontName(fontName);
      const customWeights = customFontsMap.get(baseName);

      if (customWeights && customWeights.size > 0) {
        // Return custom weights sorted from thickest to thinnest
        const weightOrder = [...WEIGHT_ORDER];
        const sortedWeights = Array.from(customWeights).sort((a, b) => {
          const indexA = weightOrder.indexOf(a);
          const indexB = weightOrder.indexOf(b);
          if (indexA === -1 && indexB === -1) return a.localeCompare(b);
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;
          return indexA - indexB;
        });
        return sortedWeights;
      }

      // Default weights for non-custom fonts (from thickest to thinnest)
      return ["Bold", "Semi-Bold", "Normal", "Light"];
    },
    [customFontsMap]
  );

  // Dynamic font weights based on selected font
  const fontWeights = useMemo(() => {
    // Get the font base directly without using getFontDisplayValue to avoid re-renders
    // If there is an active preview, use the original font from the snapshot
    let fontForWeights = "";

    if (previewSnapshotRef.current?.font) {
      fontForWeights = extractBaseFontName(previewSnapshotRef.current.font);
    } else if (selectedShapes && selectedShapes.length > 0) {
      const textShapes = selectedShapes.filter((shape: any) => shape?.props?.text);
      if (textShapes.length > 0) {
        const firstFont = textShapes[0]?.props?.font;
        fontForWeights = firstFont ? extractBaseFontName(firstFont) : "";
      }
    }

    return getAvailableWeights(fontForWeights);
  }, [getAvailableWeights, customFontsMap, selectedShapes]);

  // Get available italic fonts for the selected font family (similar to getAvailableWeights)
  const getAvailableItalicFonts = useCallback(
    async (fontName: string | null | undefined): Promise<string[]> => {
      if (!fontName) return [];

      const baseName = extractBaseFontName(fontName);
      const isCustom = customFontsMap.has(baseName);

      if (isCustom) {
        try {
          const cdnFonts = await s3CustomFontService.getLocalFontRegistry();
          const localFonts = await customFontStorage.getCustomFonts();

          // Use Set to eliminate duplicates and normalize font names
          // Create a map: normalizedName -> originalName for looking up metadata
          const normalizedToOriginalItalic = new Map<string, string>();
          const allCustomFontsSet = new Set<string>();
          cdnFonts.forEach((f) => {
            const normalizedName = normalizeFontName(f.name);
            normalizedToOriginalItalic.set(normalizedName, f.name);
            allCustomFontsSet.add(normalizedName);
          });
          localFonts.forEach((f) => {
            const normalizedName = normalizeFontName(f.name);
            normalizedToOriginalItalic.set(normalizedName, f.name);
            allCustomFontsSet.add(normalizedName);
          });
          const allCustomFonts = Array.from(allCustomFontsSet);

          // Find all italic fonts for this base name
          const italicFontsSet = new Set<string>(); // Use Set to avoid duplicates

          for (const customFontName of allCustomFonts) {
            const fontBaseName = extractBaseFontName(customFontName);
            if (fontBaseName !== baseName) continue;

            // Check if font name contains italic (including all weight + italic variations)
            const fontNameLower = customFontName.toLowerCase();
            const isItalic = isItalicFontName(customFontName);
            // Check if font is also condensed - if so, exclude it from italic fonts
            // (fonts with both condensed and italic should only appear in condensed section)
            const isCondensed = isCondensedFontNameBasic(customFontName);

            // Also check metadata - use original name to find in localFonts
            const originalNameItalic = normalizedToOriginalItalic.get(customFontName) || customFontName;
            const localFont = localFonts.find((f) => f.name === originalNameItalic);
            const isItalicFromMetadata = localFont?.style && localFont.style.toLowerCase().includes("italic");

            // Only add to italic fonts if it's italic AND NOT condensed
            // (condensed+italic fonts will appear in condensed section with proper label)
            if ((isItalic || isItalicFromMetadata) && !isCondensed) {
              italicFontsSet.add(customFontName); // Use Set to avoid duplicates
            }
          }

          const italicFonts = Array.from(italicFontsSet);

          // Sort italic fonts by weight (from thickest to thinnest)
          const weightOrder = [
            "Black",
            "Heavy",
            "Ultra-Bold",
            "ExtraBold",
            "Bold",
            "Semi-Bold",
            "Medium",
            "Normal",
            "Regular",
            "Light",
            "Extra-Light",
            "Thin",
          ];

          italicFonts.sort((a, b) => {
            // Use italic-specific extraction first, then fallback to regular extraction
            const weightA = extractWeightFromItalicFontName(a) || extractWeightFromFontName(a);
            const weightB = extractWeightFromItalicFontName(b) || extractWeightFromFontName(b);
            const normalizedA = weightA ? normalizeWeightName(weightA) : "Regular";
            const normalizedB = weightB ? normalizeWeightName(weightB) : "Regular";

            const indexA = weightOrder.indexOf(normalizedA);
            const indexB = weightOrder.indexOf(normalizedB);
            if (indexA === -1 && indexB === -1) return a.localeCompare(b);
            if (indexA === -1) return 1;
            if (indexB === -1) return -1;
            return indexA - indexB;
          });

          return italicFonts;
        } catch (error) {
          console.warn("Error getting available italic fonts:", error);
          return [];
        }
      }

      // For non-custom fonts, return empty array (they use regular italic prop)
      return [];
    },
    [customFontsMap]
  );

  // Get available condensed fonts for the selected font family (similar to getAvailableItalicFonts)
  const getAvailableCondensedFonts = useCallback(
    async (fontName: string | null | undefined): Promise<string[]> => {
      if (!fontName) return [];

      const baseName = extractBaseFontName(fontName);
      const isCustom = customFontsMap.has(baseName);

      if (isCustom) {
        try {
          const cdnFonts = await s3CustomFontService.getLocalFontRegistry();
          const localFonts = await customFontStorage.getCustomFonts();

          // Use Set to eliminate duplicates and normalize font names
          // Create a map: normalizedName -> originalName for looking up metadata
          const normalizedToOriginalCondensed = new Map<string, string>();
          const allCustomFontsSet = new Set<string>();
          cdnFonts.forEach((f) => {
            const normalizedName = normalizeFontName(f.name);
            normalizedToOriginalCondensed.set(normalizedName, f.name);
            allCustomFontsSet.add(normalizedName);
          });
          localFonts.forEach((f) => {
            const normalizedName = normalizeFontName(f.name);
            normalizedToOriginalCondensed.set(normalizedName, f.name);
            allCustomFontsSet.add(normalizedName);
          });
          const allCustomFonts = Array.from(allCustomFontsSet);

          // Find all condensed fonts for this base name
          const condensedFontsSet = new Set<string>(); // Use Set to avoid duplicates

          for (const customFontName of allCustomFonts) {
            const fontBaseName = extractBaseFontName(customFontName);
            if (fontBaseName !== baseName) continue;

            // Check if font name contains condensed (including all weight + condensed variations)
            const fontNameLower = customFontName.toLowerCase();
            const isCondensed = isCondensedFontName(customFontName);

            // Also check metadata (if we add style detection for condensed)
            // For now, rely on name detection

            if (isCondensed) {
              condensedFontsSet.add(customFontName); // Use Set to avoid duplicates
            }
          }

          const condensedFonts = Array.from(condensedFontsSet);

          // Sort condensed fonts by weight (from thickest to thinnest)
          const weightOrder = [
            "Black",
            "Heavy",
            "Ultra-Bold",
            "ExtraBold",
            "Bold",
            "Semi-Bold",
            "Medium",
            "Normal",
            "Regular",
            "Light",
            "Extra-Light",
            "Thin",
          ];

          condensedFonts.sort((a, b) => {
            // Use condensed-specific extraction first, then fallback to regular extraction
            const weightA = extractWeightFromCondensedFontName(a) || extractWeightFromFontName(a);
            const weightB = extractWeightFromCondensedFontName(b) || extractWeightFromFontName(b);
            const normalizedA = weightA ? normalizeWeightName(weightA) : "Regular";
            const normalizedB = weightB ? normalizeWeightName(weightB) : "Regular";

            const indexA = weightOrder.indexOf(normalizedA);
            const indexB = weightOrder.indexOf(normalizedB);
            if (indexA === -1 && indexB === -1) return a.localeCompare(b);
            if (indexA === -1) return 1;
            if (indexB === -1) return -1;
            return indexA - indexB;
          });

          return condensedFonts;
        } catch (error) {
          console.warn("Error getting available condensed fonts:", error);
          return [];
        }
      }

      // For non-custom fonts, return empty condensed fonts array
      return [];
    },
    [customFontsMap]
  );

  // Get available styles for the selected font
  const getAvailableStyles = useCallback(
    async (
      fontName: string | null | undefined
    ): Promise<{ italicFonts: string[]; condensedFonts: string[]; hasUnderline: boolean }> => {
      if (!fontName) return { italicFonts: [], condensedFonts: [], hasUnderline: true };

      const baseName = extractBaseFontName(fontName);
      const isCustom = customFontsMap.has(baseName);

      if (isCustom) {
        const italicFonts = await getAvailableItalicFonts(fontName);
        const condensedFonts = await getAvailableCondensedFonts(fontName);
        return { italicFonts, condensedFonts, hasUnderline: true };
      }

      // For non-custom fonts, return empty italic and condensed fonts arrays (they use regular italic prop)
      return { italicFonts: [], condensedFonts: [], hasUnderline: true };
    },
    [customFontsMap, getAvailableItalicFonts, getAvailableCondensedFonts]
  );

  // Dynamic italic fonts, condensed fonts, and styles based on selected font
  const [availableItalicFonts, setAvailableItalicFonts] = useState<string[]>([]);
  const [availableCondensedFonts, setAvailableCondensedFonts] = useState<string[]>([]);
  const [hasUnderline, setHasUnderline] = useState<boolean>(true);

  // Update available styles when font changes
  useEffect(() => {
    const updateStyles = async () => {
      const currentFont = getFontDisplayValue();
      const styles = await getAvailableStyles(currentFont);
      setAvailableItalicFonts(styles.italicFonts);
      setAvailableCondensedFonts(styles.condensedFonts);
      setHasUnderline(styles.hasUnderline);
    };
    updateStyles();
  }, [getFontDisplayValue, getAvailableStyles, customFontsMap, selectedShapes]);

  const itemClassName =
    "w-7 h-7 flex justify-center items-center cursor-pointer transition-all rounded-[4px] hover:bg-[#E1E6EB]/50";

  // Defensive font array with fallback, grouping custom fonts by base name
  const safefonts = useMemo(() => {
    if (!fonts) return ["Roboto"];
    if (!Array.isArray(fonts)) return ["Roboto"];
    if (fonts.length === 0) return ["Roboto"];

    // Separate custom fonts from regular fonts
    const regularFonts: string[] = [];
    const customFontBaseNames = new Set<string>();

    fonts.forEach((fontName: string) => {
      const baseName = extractBaseFontName(fontName);
      // Check if this is a custom font by checking if base name exists in custom fonts map
      if (customFontsMap.has(baseName)) {
        // Only add the base name once
        customFontBaseNames.add(baseName);
      } else {
        // Regular font (not custom)
        regularFonts.push(fontName);
      }
    });

    // Include custom fonts that were just uploaded but aren't in the
    // parent `fonts` prop yet (e.g. uploaded from this panel before
    // the parent refreshes, or from Brand Kit via the event).
    Array.from(customFontsMap.keys()).forEach((baseName) => {
      customFontBaseNames.add(baseName);
    });

    // Combine regular fonts with unique custom font base names
    const allFonts = [...regularFonts, ...Array.from(customFontBaseNames)];
    return allFonts.sort((a, b) => a.localeCompare(b));
  }, [fonts, customFontsMap]);

  // Filtered fonts for the custom dropdown search
  const filteredFonts = useMemo(() => {
    if (!fontSearchTerm) return safefonts;
    return safefonts.filter((f) => f.toLowerCase().includes(fontSearchTerm.toLowerCase()));
  }, [safefonts, fontSearchTerm]);

  // Only fonts that have a Brand Kit preset count as "Brand Fonts" in the
  // dropdown. Uploaded-but-no-preset fonts stay in the regular list.
  const brandPresetFamilies = useMemo(() => {
    const names = new Set<string>();
    fontPresets.forEach((p: BrandKitFontPreset) => {
      names.add(extractBaseFontName(p.fontFamily));
    });
    return names;
  }, [fontPresets]);

  // Split filtered fonts into three buckets:
  //  - brandFonts:  font families that have a Brand Kit preset (shown first with a "Brand" badge)
  //  - customFonts: user-uploaded fonts that are NOT yet part of the Brand Kit
  //                 (shown in a dedicated "Custom Fonts" group with a "Custom" badge)
  //  - otherFonts:  everything else (system / Google fonts)
  const { brandFonts, customFonts, otherFonts } = useMemo(() => {
    const brand: string[] = [];
    const custom: string[] = [];
    const other: string[] = [];
    filteredFonts.forEach((f) => {
      const base = extractBaseFontName(f);
      if (brandPresetFamilies.has(base)) {
        brand.push(f);
      } else if (customFontsMap.has(base)) {
        custom.push(f);
      } else {
        other.push(f);
      }
    });
    return { brandFonts: brand, customFonts: custom, otherFonts: other };
  }, [filteredFonts, brandPresetFamilies, customFontsMap]);

  // Re-trigger font loading when fonts are uploaded from Brand Kit
  const [fontRefreshCounter, setFontRefreshCounter] = useState(0);
  useEffect(() => {
    const handleFontsUpdated = () => {
      // Invalidate s3CustomFontService cache so it fetches fresh data
      s3CustomFontService.clearCache();
      setFontRefreshCounter((c) => c + 1);
    };
    window.addEventListener("custom-fonts-updated", handleFontsUpdated);
    return () => window.removeEventListener("custom-fonts-updated", handleFontsUpdated);
  }, []);

  // Load custom fonts and build font family map
  useEffect(() => {
    const loadCustomFonts = async () => {
      try {
        // Get fonts from S3 CDN
        const cdnFonts = await s3CustomFontService.getLocalFontRegistry();

        // Get fonts from local storage
        const localFonts = await customFontStorage.getCustomFonts();

        // Combine all custom font names and normalize them
        // Create a map: normalizedName -> originalName for looking up metadata
        const normalizedToOriginal = new Map<string, string>();
        const allCustomFontNames = new Set<string>();
        cdnFonts.forEach((font) => {
          const normalizedName = normalizeFontName(font.name);
          normalizedToOriginal.set(normalizedName, font.name);
          allCustomFontNames.add(normalizedName);
        });
        localFonts.forEach((font) => {
          const normalizedName = normalizeFontName(font.name);
          normalizedToOriginal.set(normalizedName, font.name);
          allCustomFontNames.add(normalizedName);
        });

        // Build a map: baseFontName -> Set of weights
        const fontFamilyMap = new Map<string, Set<string>>();
        // Build a map: fontName -> detected weight
        const nameToWeightMap = new Map<string, string>();

        // Process fonts and detect weights
        for (const fontName of Array.from(allCustomFontNames)) {
          const baseName = extractBaseFontName(fontName);

          // Check if this font is italic - if so, skip adding it to weights
          const fontNameLower = fontName.toLowerCase();
          const isItalic = isItalicFontName(fontName);

          // Also check metadata - use original name to find in localFonts
          const originalName = normalizedToOriginal.get(fontName) || fontName;
          const localFont = localFonts.find((f) => f.name === originalName);
          const isItalicFromMetadata = localFont?.style && localFont.style.toLowerCase().includes("italic");

          // Skip italic fonts - they should not appear in weights, only in styles
          if (isItalic || isItalicFromMetadata) {
            // Still store the weight mapping for reference, but don't add to weights list
            // Prioritize weight extraction from font name over stored metadata
            let weight: string | null = null;
            // Try italic-specific extraction first, then condensed, then regular
            weight =
              extractWeightFromItalicFontName(fontName) ||
              extractWeightFromCondensedFontName(fontName) ||
              extractWeightFromFontName(fontName);
            if (weight) {
              weight = normalizeWeightName(weight);
            } else if (localFont?.weight) {
              // Fallback to stored metadata if name doesn't contain a weight
              weight = normalizeWeightName(localFont.weight);
            }
            if (weight) {
              nameToWeightMap.set(fontName, weight);
            }
            continue; // Skip adding to fontFamilyMap (weights)
          }

          if (!fontFamilyMap.has(baseName)) {
            fontFamilyMap.set(baseName, new Set());
          }

          // Prioritize weight extraction from font name over stored metadata
          // This ensures fonts named "Heavy" show as "Heavy" and "extrabold" show as "ExtraBold"
          let weight: string | null = null;

          // First try to extract weight from font name
          const extractedWeightFromName = extractWeightFromFontName(fontName);
          if (extractedWeightFromName) {
            weight = normalizeWeightName(extractedWeightFromName);
          } else {
            // Fallback to stored metadata if name doesn't contain a weight
            const originalNameForWeight = normalizedToOriginal.get(fontName) || fontName;
            const localFontForWeight = localFonts.find((f) => f.name === originalNameForWeight);
            if (localFontForWeight?.weight) {
              weight = normalizeWeightName(localFontForWeight.weight);
            }
          }

          // Only add weight if we found one - don't add a default "Normal" if no weight is detected
          // This prevents adding non-existent weights to the font family
          if (weight) {
            fontFamilyMap.get(baseName)!.add(weight);
            nameToWeightMap.set(fontName, weight);
          } else {
            // Still store the font name mapping even if we couldn't detect weight
            // This allows the font to be used, but it won't appear in the weights dropdown
            nameToWeightMap.set(fontName, "Unknown");
          }
        }

        setCustomFontsMap(fontFamilyMap);
        setFontNameToWeightMap(nameToWeightMap);
      } catch (error) {
        console.error("Error loading custom fonts:", error);
      }
    };

    loadCustomFonts();
  }, [fonts, fontRefreshCounter]);

  // Check if fonts are ready
  useEffect(() => {
    const timer = setTimeout(() => {
      if (fonts && Array.isArray(fonts) && fonts.length > 0) {
        setFontsLoading(false);
        setFontLoadError(false);
      } else if (fonts === null || fonts === undefined) {
        // Still waiting for fonts
        setFontsLoading(true);
      } else {
        // Fonts loaded but empty or invalid
        setFontsLoading(false);
        setFontLoadError(true);
      }
    }, 100); // Small delay to prevent flashing

    return () => clearTimeout(timer);
  }, [fonts]);

  const updateShapesProps = useCallback(
    (newProps: any) => {
      if (!selectedShapes || !Array.isArray(selectedShapes) || selectedShapes.length === 0) {
        return;
      }

      if (!editor || typeof editor.updateShape !== "function") {
        console.warn("Editor not available for shape updates");
        return;
      }

      try {
        // Filter out undefined values to avoid validation errors
        const filteredProps = Object.fromEntries(Object.entries(newProps).filter(([_, value]) => value !== undefined));

        const updatedShapes = selectedShapes.map((shape) => {
          if (shape?.props?.text) {
            return {
              ...shape,
              props: {
                ...shape.props,
                ...filteredProps,
              },
            };
          }
          return shape;
        });

        updatedShapes.forEach((shape) => {
          if (shape?.id) {
            editor.updateShape(shape);
          }
        });

        if (selectedShape?.id) {
          const updatedSelectedShape = updatedShapes.find((shape) => shape?.id === selectedShape.id);
          if (updatedSelectedShape && setSelectedShape) {
            setSelectedShape(updatedSelectedShape);
          }
        }
      } catch (error) {
        console.error("Failed to update shape properties:", error);
      }
    },
    [selectedShapes, editor, selectedShape, setSelectedShape]
  );

  // Drag-to-adjust handlers for typography icons
  const lineHeightDrag = useDragToAdjust(
    () => {
      const fontSizePx = parseFloat(displayFontSize);
      const em = selectedShape?.props?.lineHeight ?? 1;
      return !isNaN(fontSizePx) && fontSizePx > 0 ? Math.round(em * fontSizePx) : 0;
    },
    (px) => {
      const fontSizePx = parseFloat(displayFontSize);
      if (!isNaN(fontSizePx) && fontSizePx > 0) {
        updateShapesProps({ lineHeight: px / fontSizePx });
      }
    },
    { min: 0, step: 1 }
  );

  const letterSpacingDrag = useDragToAdjust(
    () => Number(selectedShape?.props?.letterSpacing ?? 0),
    (v) => updateShapesProps({ letterSpacing: Math.round(v * 10) / 10 }),
    { min: -7.5, max: 50, step: 0.1, integer: false }
  );

  const saveLastSelectedFont = useCallback((font: string) => {
    try {
      localStorage.setItem("lastSelectedFont", font);
    } catch (error) {
      console.warn("Failed to save last selected font:", error);
    }
  }, []);

  const handleFontChange = useCallback(
    async (newValue: any) => {
      if (!newValue) {
        const font = selectedShape?.props?.font || "Roboto";
        if (setSelectedFont && typeof setSelectedFont === "function") {
          setSelectedFont(font);
        }
        updateShapesProps({ font });
        saveLastSelectedFont(font);
        return;
      }

      // Check if the newValue is already a full font name (not just base name)
      // If it exists in the custom fonts list, use it directly
      const baseName = extractBaseFontName(newValue);
      const isCustomBaseName = customFontsMap.has(baseName);

      // Check if newValue is already a complete font name (has weight in it)
      const hasWeight = extractWeightFromFontName(newValue) !== null;

      if (isCustomBaseName && !hasWeight) {
        // It's a custom font base name without weight, we need to find the actual font file to use
        // Try to find a font with the current weight, or default to the first available
        try {
          const cdnFonts = await s3CustomFontService.getLocalFontRegistry();
          const localFonts = await customFontStorage.getCustomFonts();
          const allCustomFonts = [...cdnFonts.map((f) => f.name), ...localFonts.map((f) => f.name)];

          // Check if newValue already exists as a complete font name
          if (allCustomFonts.includes(newValue)) {
            // It's already a complete font name, use it directly
            const fontNameLower = newValue.toLowerCase();
            const isItalicFont = fontNameLower.includes("italic") || fontNameLower.includes("it");

            if (setSelectedFont && typeof setSelectedFont === "function") {
              setSelectedFont(newValue);
            }

            // For custom fonts, always set italic to "not-italic"
            // Italic fonts are italic by design (they're separate font files), so they don't need the italic prop
            updateShapesProps({ font: newValue, italic: "not-italic" });
            saveLastSelectedFont(newValue);
            return;
          }

          // Get current font weight if available
          const currentFont = selectedShape?.props?.font || "";
          const currentWeight = currentFont ? extractWeightFromFontName(currentFont) : null;
          const normalizedCurrentWeight = currentWeight ? normalizeWeightName(currentWeight) : null;

          // Try to find a font with the same base name and current weight
          let matchingFont = allCustomFonts.find((fontName) => {
            const fontBaseName = extractBaseFontName(fontName);
            const fontWeight = extractWeightFromFontName(fontName);
            const normalizedWeight = fontWeight ? normalizeWeightName(fontWeight) : "Normal";
            return fontBaseName === baseName && normalizedWeight === normalizedCurrentWeight;
          });

          // If no match with current weight, use the first available font of this family
          if (!matchingFont) {
            matchingFont = allCustomFonts.find((fontName) => {
              const fontBaseName = extractBaseFontName(fontName);
              return fontBaseName === baseName;
            });
          }

          if (matchingFont) {
            // Check if matching font is italic
            const fontNameLower = matchingFont.toLowerCase();
            const isItalicFont = fontNameLower.includes("italic") || fontNameLower.includes("it");

            if (setSelectedFont && typeof setSelectedFont === "function") {
              setSelectedFont(matchingFont);
            }

            // For custom fonts, explicitly set italic to "not-italic" unless it's an italic font file
            // Italic fonts will have italic: "not-italic" because they're italic by design
            updateShapesProps({ font: matchingFont, italic: "not-italic" });
            saveLastSelectedFont(matchingFont);
            return;
          }
        } catch (error) {
          console.error("Error finding custom font:", error);
        }
      } else if (hasWeight || !isCustomBaseName) {
        // It's either a complete font name with weight, or not a custom font
        // Use it directly without further lookup
        const font = newValue || selectedShape?.props?.font || "Roboto";
        if (setSelectedFont && typeof setSelectedFont === "function") {
          setSelectedFont(font);
        }
        // For custom fonts, ensure italic is "not-italic" (custom fonts don't use italic prop unless it's an italic font file)
        if (isCustomBaseName) {
          updateShapesProps({ font, italic: "not-italic" });
        } else {
          updateShapesProps({ font });
        }
        saveLastSelectedFont(font);
        return;
      }

      // Fallback: use the value directly
      const font = newValue || selectedShape?.props?.font || "Roboto";
      if (setSelectedFont && typeof setSelectedFont === "function") {
        setSelectedFont(font);
      }
      // For custom fonts, ensure italic is "not-italic"
      const fallbackBaseName = extractBaseFontName(font);
      if (customFontsMap.has(fallbackBaseName)) {
        updateShapesProps({ font, italic: "not-italic" });
      } else {
        updateShapesProps({ font });
      }
    },
    [setSelectedFont, updateShapesProps, selectedShape, customFontsMap]
  );

  // --- Preview helpers (must be after updateShapesProps is defined) ---
  const beginPreview = useCallback(() => {
    if (previewSnapshot || !selectedShape) return;
    // Capture each selected shape's original props individually so a
    // cancelled hover restores every shape to its own font (not to the
    // primary shape's font).
    const byShape = (selectedShapes || [])
      .filter((s: any) => s?.id && s?.type && s?.props?.text !== undefined)
      .map((s: any) => ({
        id: s.id,
        type: s.type,
        font: s.props?.font,
        fontWeight: s.props?.fontWeight,
        italic: s.props?.italic,
      }));
    const snapshot = {
      font: selectedShape.props?.font,
      fontWeight: selectedShape.props?.fontWeight,
      italic: selectedShape.props?.italic,
      byShape,
    };
    setPreviewSnapshot(snapshot);
    previewSnapshotRef.current = snapshot; // Also update the ref
  }, [previewSnapshot, selectedShape, selectedShapes]);

  const restorePreview = useCallback(() => {
    if (!previewSnapshot) return;
    // Restore each shape to its OWN original props so multi-selections with
    // different fonts go back to their individual fonts instead of all
    // adopting the primary shape's font. Wrap in `editor.run(..., { history:
    // "ignore" })` so preview hover/restore cycles never pollute the undo
    // stack.
    if (editor && previewSnapshot.byShape && previewSnapshot.byShape.length > 0) {
      editor.run(
        () => {
          previewSnapshot.byShape!.forEach((snap) => {
            const current = editor.getShape(snap.id as any);
            if (!current) return;
            editor.updateShape({
              id: snap.id as any,
              type: snap.type as any,
              props: {
                ...(current.props as any),
                font: snap.font,
                fontWeight: snap.fontWeight,
                italic: snap.italic,
              },
            } as any);
          });
        },
        { history: "ignore" }
      );
    } else {
      // Fallback: single-shape snapshot (older shape set). Keep the original
      // behaviour of applying the representative props to the selection, but
      // still run under history: "ignore" so the restore never pollutes the
      // undo stack.
      if (editor) {
        editor.run(
          () => {
            updateShapesProps({
              font: previewSnapshot.font,
              fontWeight: previewSnapshot.fontWeight,
              italic: previewSnapshot.italic,
            });
          },
          { history: "ignore" }
        );
      } else {
        updateShapesProps({
          font: previewSnapshot.font,
          fontWeight: previewSnapshot.fontWeight,
          italic: previewSnapshot.italic,
        });
      }
    }

    // Clean the snapshot after a small delay to update the UI
    setTimeout(() => {
      setPreviewSnapshot(null);
      previewSnapshotRef.current = null;
    }, 50);
  }, [previewSnapshot, updateShapesProps, editor]);

  // Track previous open state to detect closures triggered externally (e.g., canvas click)
  const prevWeightMenuOpenRef = useRef<boolean>(false);
  useEffect(() => {
    const prevOpen = prevWeightMenuOpenRef.current;
    if (prevOpen && !weightMenuOpen) {
      // Menu has just closed
      if (!selectionCommittedRef.current) {
        restorePreview();
      } else {
        setPreviewSnapshot(null);
      }
      setHoveredMenuItem(null);
      selectionCommittedRef.current = false;
    }
    prevWeightMenuOpenRef.current = weightMenuOpen;
  }, [weightMenuOpen, restorePreview]);

  // Track font dropdown state to restore preview when closed without selection
  useEffect(() => {
    const prevOpen = prevFontDropdownOpenRef.current;
    if (prevOpen && !fontDropdownOpen) {
      // Dropdown has just closed
      if (!selectionCommittedRef.current) {
        restorePreview();
      } else {
        setPreviewSnapshot(null);
        previewSnapshotRef.current = null;
      }
      selectionCommittedRef.current = false;
    }
    prevFontDropdownOpenRef.current = fontDropdownOpen;
  }, [fontDropdownOpen, restorePreview, previewSnapshot]);

  // Outside-click handler for custom font dropdown
  useEffect(() => {
    if (!fontSelectorOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (fontDropdownWrapperRef.current && !fontDropdownWrapperRef.current.contains(e.target as Node)) {
        setFontSelectorOpen(false);
        setFontDropdownOpen(false);
        setFontToDelete(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [fontSelectorOpen]);

  // Cleanup: Restore preview when the component is unmounted. Uses the
  // per-shape snapshot so each shape returns to its own font, and runs under
  // `history: "ignore"` so the restore does not push undo entries.
  useEffect(() => {
    return () => {
      const snapshot = previewSnapshotRef.current;
      const wasCommitted = selectionCommittedRef.current;

      if (snapshot && !wasCommitted && editor) {
        try {
          editor.run(
            () => {
              if (snapshot.byShape && snapshot.byShape.length > 0) {
                snapshot.byShape.forEach((snap) => {
                  const current = editor.getShape(snap.id as any);
                  if (!current) return;
                  editor.updateShape({
                    id: snap.id as any,
                    type: snap.type as any,
                    props: {
                      ...(current.props as any),
                      font: snap.font,
                      fontWeight: snap.fontWeight,
                      italic: snap.italic,
                    },
                  } as any);
                });
              } else if (selectedShapes) {
                // Legacy fallback for snapshots that only carry representative values.
                selectedShapes.forEach((shape: any) => {
                  if (shape?.props?.text && shape?.id) {
                    editor.updateShape({
                      ...shape,
                      props: {
                        ...shape.props,
                        font: snapshot.font,
                        fontWeight: snapshot.fontWeight,
                        italic: snapshot.italic,
                      },
                    });
                  }
                });
              }
            },
            { history: "ignore" }
          );
        } catch (error) {
          console.error("Error restoring preview on unmount:", error);
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - cleanup only executes when the component is unmounted

  const applyWeightPreview = useCallback(
    (weight: string) => {
      beginPreview();
      // Wrap every preview shape update in `editor.run(..., { history: "ignore" })`
      // so hovering through the weight menu never creates undo entries.
      const runPreview = (fn: () => void) => {
        if (editor) {
          editor.run(fn, { history: "ignore" });
        } else {
          fn();
        }
      };
      const selectedFontName = selectedShape?.props?.font || "";
      const baseName = extractBaseFontName(selectedFontName);
      const isCustom = customFontsMap.has(baseName);
      const normalizedTarget = normalizeWeightName(weight === "Regular" ? "Normal" : weight);

      if (isCustom) {
        // Try to find a custom font variant matching the weight (preview only; no save/commit)
        let matchingFont: string | null = null;
        fontNameToWeightMap.forEach((w, fontName) => {
          const nameLower = fontName.toLowerCase();
          const isItalic =
            nameLower.includes("italic") ||
            nameLower.includes(" italic") ||
            nameLower.endsWith("it") ||
            nameLower.endsWith("-it") ||
            nameLower.endsWith("_it") ||
            nameLower.includes(" oblique") ||
            nameLower.endsWith("oblique");
          const isCond =
            nameLower.includes("condensed") ||
            nameLower.includes("cond") ||
            nameLower.includes("cn") ||
            nameLower.includes("semicn") ||
            nameLower.includes("semicond");
          // Skip italic/condensed variants when searching for a base-weight preview
          if (isItalic || isCond) return;

          if (extractBaseFontName(fontName) === baseName && normalizeWeightName(w) === normalizedTarget) {
            matchingFont = fontName;
          }
          // Treat Regular/Normal as equivalents for matching
          if (
            !matchingFont &&
            extractBaseFontName(fontName) === baseName &&
            ((normalizedTarget === "Normal" && normalizeWeightName(w) === "Regular") ||
              (normalizedTarget === "Regular" && normalizeWeightName(w) === "Normal"))
          ) {
            matchingFont = fontName;
          }
        });
        if (matchingFont) {
          runPreview(() => updateShapesProps({ font: matchingFont, italic: "not-italic" }));
          return;
        }
      }

      // Fallback: just set fontWeight numeric for non-custom or if no variant found
      const weightValue = getFontWeightValue(weight === "Regular" ? "Normal" : weight);
      if (weightValue !== null) {
        runPreview(() => updateShapesProps({ fontWeight: weightValue }));
      }
    },
    [beginPreview, customFontsMap, fontNameToWeightMap, selectedShape, updateShapesProps, editor]
  );

  const applyStylePreview = useCallback(
    (fontOrStyle: string) => {
      beginPreview();
      const apply = () => {
        if (fontOrStyle === "Italic" || fontOrStyle === "italic" || fontOrStyle === "not-italic") {
          updateShapesProps({ italic: fontOrStyle === "Italic" || fontOrStyle === "italic" ? "italic" : "not-italic" });
        } else {
          updateShapesProps({ font: fontOrStyle });
        }
      };
      if (editor) {
        editor.run(apply, { history: "ignore" });
      } else {
        apply();
      }
    },
    [beginPreview, updateShapesProps, editor]
  );

  const applyFontPreview = useCallback(
    (fontName: string) => {
      beginPreview();
      // Every preview application is wrapped with `history: "ignore"` so
      // hovering through the font dropdown never pollutes the undo stack.
      const runPreview = (fn: () => void) => {
        if (editor) {
          editor.run(fn, { history: "ignore" });
        } else {
          fn();
        }
      };

      const baseName = extractBaseFontName(fontName);
      const isCustom = customFontsMap.has(baseName);

      if (isCustom) {
        // For custom fonts, try to match the current weight
        // Use the snapshot font if available (original font before preview started)
        const originalFont = previewSnapshot?.font || selectedShape?.props?.font || "";
        const currentWeight = originalFont ? extractWeightFromFontName(originalFont) : null;
        const normalizedCurrentWeight = currentWeight ? normalizeWeightName(currentWeight) : null;

        // Try to find a font with the same base name and current weight
        let matchingFont: string | null = null;

        // Get all fonts from the font map
        const allCustomFontNames = new Set<string>();
        fontNameToWeightMap.forEach((weight, name) => {
          if (extractBaseFontName(name) === baseName) {
            allCustomFontNames.add(name);
          }
        });

        // Try to find a font with the current weight
        if (normalizedCurrentWeight) {
          for (const customFontName of Array.from(allCustomFontNames)) {
            const customFontWeight = fontNameToWeightMap.get(customFontName);
            if (customFontWeight && normalizeWeightName(customFontWeight) === normalizedCurrentWeight) {
              matchingFont = customFontName;
              break;
            }
          }
        }

        // If no match with current weight, use the first available font of this family
        if (!matchingFont && allCustomFontNames.size > 0) {
          matchingFont = Array.from(allCustomFontNames)[0];
        }

        if (matchingFont) {
          runPreview(() => updateShapesProps({ font: matchingFont, italic: "not-italic" }));
        } else {
          // Fallback to the base name if no matching font found
          runPreview(() => updateShapesProps({ font: fontName, italic: "not-italic" }));
        }
      } else {
        // For non-custom fonts (system/Google fonts), apply the font while
        // preserving the current weight so Brand Kit fonts with a specific
        // weight don't get reset to Regular on hover/select.
        runPreview(() => updateShapesProps({ font: fontName }));
      }
    },
    [beginPreview, customFontsMap, fontNameToWeightMap, previewSnapshot, selectedShape, updateShapesProps, editor]
  );

  const closeWeightMenu = useCallback(
    (commit = false) => {
      if (!commit) {
        restorePreview();
      } else {
        setPreviewSnapshot(null);
      }
      selectionCommittedRef.current = false;
      setWeightMenuAnchor(null);
    },
    [restorePreview]
  );

  const handleFontWeightChange = useCallback(
    (newValue: any) => {
      if (!newValue) return;

      // Get the actual font name from selectedShape, not the display value
      const actualFont = selectedShape?.props?.font || "";
      const currentFontDisplay = getFontDisplayValue();
      const baseFontName = extractBaseFontName(actualFont || currentFontDisplay);
      const isCustom = isCustomFont(actualFont || currentFontDisplay);

      if (isCustom && baseFontName) {
        // For custom fonts, we need to find the font file with the selected weight
        // and change the font name to that specific font file
        const customWeights = customFontsMap.get(baseFontName);

        // Normalize the weight name for comparison (Normal -> Regular)
        const normalizedWeightForCheck = normalizeWeightName(newValue);
        // Check if the weight is available, considering Regular/Normal equivalence
        const weightAvailable =
          customWeights &&
          (customWeights.has(newValue) ||
            customWeights.has(normalizedWeightForCheck) ||
            (normalizedWeightForCheck === "Normal" && customWeights.has("Regular")) ||
            (normalizedWeightForCheck === "Regular" && customWeights.has("Normal")));

        if (weightAvailable) {
          // Find the font name that matches this weight
          // We need to search through all custom fonts to find the one with this weight
          const findFontWithWeight = async () => {
            try {
              const cdnFonts = await s3CustomFontService.getLocalFontRegistry();
              const localFonts = await customFontStorage.getCustomFonts();
              // Create a map: normalizedName -> originalName for looking up metadata
              const normalizedToOriginalWeight = new Map<string, string>();
              const allCustomFonts = [
                ...cdnFonts.map((f) => {
                  const normalized = normalizeFontName(f.name);
                  normalizedToOriginalWeight.set(normalized, f.name);
                  return normalized;
                }),
                ...localFonts.map((f) => {
                  const normalized = normalizeFontName(f.name);
                  normalizedToOriginalWeight.set(normalized, f.name);
                  return normalized;
                }),
              ];

              // Normalize the selected weight for comparison
              // Convert "Normal" to "Regular" for matching with available weights
              let normalizedSelectedWeight = normalizeWeightName(newValue);
              // If normalized is "Normal" but we're looking for a custom font, try "Regular" instead
              if (
                normalizedSelectedWeight === "Normal" &&
                customWeights &&
                customWeights.has("Regular") &&
                !customWeights.has("Normal")
              ) {
                normalizedSelectedWeight = "Regular";
              }

              // Find font with matching base name and weight
              // Try exact match first, then try Regular/Normal equivalence
              // Check metadata first, then fallback to name extraction
              // Exclude italic fonts when selecting weights
              let matchingFont = allCustomFonts.find((fontName) => {
                const fontBaseName = extractBaseFontName(fontName);
                if (fontBaseName !== baseFontName) return false;

                // Skip italic and condensed fonts when selecting weights
                const fontNameLower = fontName.toLowerCase();
                if (fontNameLower.includes("italic") || fontNameLower.includes("it")) {
                  return false;
                }
                if (
                  fontNameLower.includes("condensed") ||
                  fontNameLower.includes("cond") ||
                  fontNameLower.includes("cn") ||
                  fontNameLower.includes("semicn") ||
                  fontNameLower.includes("semicond")
                ) {
                  return false;
                }

                // Check metadata for italic style - use original name to find in localFonts
                const originalNameForWeight = normalizedToOriginalWeight.get(fontName) || fontName;
                const localFont = localFonts.find((f) => f.name === originalNameForWeight);
                if (localFont?.style && localFont.style.toLowerCase().includes("italic")) {
                  return false;
                }

                // Prioritize weight extraction from font name over stored metadata
                let normalizedWeight: string | null = null;
                const extractedWeightFromName = extractWeightFromFontName(fontName);
                if (extractedWeightFromName) {
                  normalizedWeight = normalizeWeightName(extractedWeightFromName);
                } else if (localFont?.weight) {
                  // Fallback to stored metadata if name doesn't contain a weight
                  normalizedWeight = normalizeWeightName(localFont.weight);
                } else {
                  normalizedWeight = "Normal";
                }

                // Exact match
                if (normalizedWeight === normalizedSelectedWeight) {
                  return true;
                }

                return false;
              });

              // If no exact match, try Regular/Normal equivalence
              if (!matchingFont) {
                matchingFont = allCustomFonts.find((fontName) => {
                  const fontBaseName = extractBaseFontName(fontName);
                  if (fontBaseName !== baseFontName) return false;

                  // Skip italic fonts when selecting weights
                  const fontNameLower = fontName.toLowerCase();
                  if (fontNameLower.includes("italic") || fontNameLower.includes("it")) {
                    return false;
                  }

                  // Check metadata for italic style - use original name to find in localFonts
                  const originalNameForWeight2 = normalizedToOriginalWeight.get(fontName) || fontName;
                  const localFont = localFonts.find((f) => f.name === originalNameForWeight2);
                  if (localFont?.style && localFont.style.toLowerCase().includes("italic")) {
                    return false;
                  }

                  // Try to get weight from metadata first
                  let normalizedWeight: string | null = null;
                  if (localFont?.weight) {
                    normalizedWeight = normalizeWeightName(localFont.weight);
                  }

                  // If no weight in metadata, extract from name
                  if (!normalizedWeight) {
                    const fontWeight = extractWeightFromFontName(fontName);
                    normalizedWeight = fontWeight ? normalizeWeightName(fontWeight) : "Normal";
                  }

                  // Handle Regular/Normal equivalence
                  const weightMatches =
                    (normalizedWeight === "Regular" && normalizedSelectedWeight === "Normal") ||
                    (normalizedWeight === "Normal" && normalizedSelectedWeight === "Regular");

                  if (weightMatches) {
                    return true;
                  }

                  return false;
                });
              }

              // Log all fonts with matching base name for debugging
              const fontsWithBaseName = allCustomFonts.filter((fontName) => {
                const fontBaseName = extractBaseFontName(fontName);
                return fontBaseName === baseFontName;
              });

              if (matchingFont) {
                // Check if matching font is italic (shouldn't be, since we exclude italic fonts when selecting weights)
                const matchingFontLower = matchingFont.toLowerCase();
                const isMatchingFontItalic = matchingFontLower.includes("italic") || matchingFontLower.includes("it");

                // Change to the specific font file with the selected weight
                // Use updateShapesProps directly to ensure the font is updated immediately
                // Update both font and fontWeight in a single call for consistency
                const weightValue = getFontWeightValue(newValue);
                const propsToUpdate: any = { font: matchingFont };
                if (weightValue !== null) {
                  propsToUpdate.fontWeight = weightValue;
                }
                // Always set italic to "not-italic" for custom fonts when selecting a weight
                // Custom fonts don't use the italic prop unless it's an italic font file
                propsToUpdate.italic = "not-italic";
                updateShapesProps(propsToUpdate);

                // Also update selectedFont state if available
                if (setSelectedFont && typeof setSelectedFont === "function") {
                  setSelectedFont(matchingFont);
                }

                saveLastSelectedFont(matchingFont);

                // Force a small delay to ensure state updates propagate
                setTimeout(() => {
                  console.log(`✅ Font change completed. Current font should be: "${matchingFont}"`);
                }, 100);
              } else {
                console.warn(`⚠️ Could not find font with base "${baseFontName}" and weight "${newValue}"`);
                console.warn(`Available fonts with base "${baseFontName}":`, fontsWithBaseName);
                console.warn(`Looking for weight: "${normalizedSelectedWeight}"`);
                // Fallback: just update fontWeight if we can't find the exact font
                const weightValue = getFontWeightValue(newValue);
                if (weightValue !== null) {
                  updateShapesProps({ fontWeight: weightValue });
                }
              }
            } catch (error) {
              console.error("Error finding font with weight:", error);
              // Fallback: just update fontWeight
              const weightValue = getFontWeightValue(newValue);
              if (weightValue !== null) {
                updateShapesProps({ fontWeight: weightValue });
              }
            }
          };

          findFontWithWeight();
        } else {
          console.warn(
            `⚠️ Weight "${newValue}" not available for font "${baseFontName}". Available weights:`,
            Array.from(customWeights || [])
          );
          // Weight not available for this custom font, just update fontWeight if possible
          const weightValue = getFontWeightValue(newValue);
          if (weightValue !== null) {
            updateShapesProps({ fontWeight: weightValue });
          }
        }
      } else {
        // For non-custom fonts, just update the fontWeight property
        const weightValue = getFontWeightValue(newValue);
        if (weightValue !== null) {
          updateShapesProps({ fontWeight: weightValue });
        } else {
          // Fallback to original getFontWeight for backward compatibility
          try {
            const fontWeight = getFontWeight(newValue);
            updateShapesProps({ fontWeight });
          } catch {
            // Ignore if weight conversion fails (e.g. "Heavy" -> 900)
          }
        }
      }
    },
    [
      updateShapesProps,
      getFontDisplayValue,
      isCustomFont,
      customFontsMap,
      handleFontChange,
      selectedShape,
      saveLastSelectedFont,
    ]
  );

  // Commit helpers for popper menu (must be after handleFontWeightChange)
  const commitWeight = useCallback(
    (value: string) => {
      const normalized = value === "Regular" ? "Normal" : value;
      selectionCommittedRef.current = true;
      setPreviewSnapshot(null);
      handleFontWeightChange(normalized);
      closeWeightMenu(true);
    },
    [closeWeightMenu, handleFontWeightChange]
  );

  const commitFontChange = useCallback(
    (fontName: string) => {
      selectionCommittedRef.current = true;
      setPreviewSnapshot(null);
      if (setSelectedFont) setSelectedFont(fontName);
      updateShapesProps({ font: fontName, fontWeight: undefined, italic: "not-italic" });
      saveLastSelectedFont(fontName);
      closeWeightMenu(true);
    },
    [closeWeightMenu, saveLastSelectedFont, setSelectedFont, updateShapesProps]
  );

  const commitItalicToggle = useCallback(() => {
    selectionCommittedRef.current = true;
    setPreviewSnapshot(null);
    const currentItalic = selectedShape?.props?.italic;
    updateShapesProps({ italic: currentItalic === "italic" ? "not-italic" : "italic" });
    closeWeightMenu(true);
  }, [closeWeightMenu, selectedShape?.props?.italic, updateShapesProps]);

  const commitUnderlineToggle = useCallback(() => {
    selectionCommittedRef.current = true;
    setPreviewSnapshot(null);
    const currentUnderline = selectedShape?.props?.underline;
    updateShapesProps({ underline: currentUnderline === "underline" ? "none" : "underline" });
    closeWeightMenu(true);
  }, [closeWeightMenu, selectedShape?.props?.underline, updateShapesProps]);

  // Global pointerdown to catch clicks on canvas (outside sidebar/popover)
  useEffect(() => {
    if (!weightMenuOpen) return;
    const handlePointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      const inAnchor = weightMenuAnchor?.contains(target as Node) ?? false;
      const inPopper = popperRef.current?.contains(target as Node) ?? false;
      if (!inAnchor && !inPopper) {
        closeWeightMenu(false);
      }
    };
    window.addEventListener("pointerdown", handlePointerDown, { capture: true });
    return () => window.removeEventListener("pointerdown", handlePointerDown, { capture: true });
  }, [weightMenuOpen, weightMenuAnchor, closeWeightMenu]);

  const handleFontUpload = useCallback(
    async (file: File) => {
      let fontName = file.name.split(".")[0];
      if (!fontName) return; // Exit if no name is provided

      // Normalize font name: "SF-Pro-Display-Regular" -> "SFProDisplay-Regular"
      fontName = normalizeFontName(fontName);

      setFontsLoading(true);

      // Show upload starting notification
      showNotification(`🔄 Uploading font "${fontName}"...`, "info");

      try {
        // Create font face for immediate use (quick feedback)
        const reader = new FileReader();
        reader.onload = async (e) => {
          const dataUrl = e.target?.result as string;
          const fontFace = new FontFace(fontName, `url(${dataUrl})`);

          try {
            await fontFace.load();
            document.fonts.add(fontFace);
            setSelectedFont(fontName);

            // Add the new font to the fonts array and sort alphabetically
            if (Array.isArray(fonts)) {
              fonts.push(fontName);
              fonts.sort((a: string, b: string) => a.localeCompare(b));
            }

            console.log(`✅ Font "${fontName}" loaded immediately for preview`);
          } catch (error) {
            console.warn("Failed to load font for immediate preview:", error);
          }
        };
        reader.readAsDataURL(file);

        // Upload to S3 CDN (primary storage)
        const { s3CustomFontService } = await import("../../services/s3CustomFontService");
        let fontData;
        try {
          fontData = await s3CustomFontService.uploadFontToCDN(file, fontName);
        } catch (uploadError) {
          console.error(`❌ Failed to upload font "${fontName}" to S3:`, uploadError);
          throw uploadError;
        }

        // Also store locally as fallback for offline access
        try {
          const { customFontStorage } = await import("../../services/customFontStorage");
          const dataUrl = await new Promise<string>((resolve, reject) => {
            const fallbackReader = new FileReader();
            fallbackReader.onload = () => resolve(fallbackReader.result as string);
            fallbackReader.onerror = () => reject(new Error("Failed to read file"));
            fallbackReader.readAsDataURL(file);
          });

          // Prioritize weight extraction from font name over file metadata
          // This ensures fonts named "Heavy" show as "Heavy" and "extrabold" show as "ExtraBold"
          let detectedWeight: string | null = null;
          const extractedWeightFromName = extractWeightFromFontName(fontName);
          if (extractedWeightFromName) {
            detectedWeight = normalizeWeightName(extractedWeightFromName);
          } else {
            // Fallback to file metadata if name doesn't contain a weight
            detectedWeight = await detectFontWeightFromFile(file, fontName);
            if (detectedWeight) {
              console.log(`✅ Weight detected from font file: "${detectedWeight}"`);
            } else {
              detectedWeight = "Normal";
            }
          }

          let detectedStyle = await detectFontStyleFromFile(file, fontName);

          // Fallback to extracting style from font name if not detected from file
          if (!detectedStyle) {
            const fontNameLower = fontName.toLowerCase();
            if (fontNameLower.includes("italic") || fontNameLower.includes("it")) {
              detectedStyle = "italic";
            } else {
              detectedStyle = "normal";
            }
          }

          await customFontStorage.storeCustomFont({
            name: fontName,
            dataUrl: dataUrl,
            mimeType: file.type || "font/ttf",
            weight: detectedWeight.toLowerCase(),
            style: detectedStyle,
            uploadedAt: Date.now(),
          });
        } catch (localError) {
          console.warn("Failed to store font locally (fallback):", localError);
          // Don't fail the entire operation if local storage fails
        }

        // Wait a bit for backend sync to complete
        await new Promise((resolve) => setTimeout(resolve, 500));

        setFontsLoading(false);
        setFontLoadError(false);

        // Reload custom fonts to ensure backend sync and update the map
        try {
          const cdnFonts = await s3CustomFontService.getLocalFontRegistry();
          const localFonts = await customFontStorage.getCustomFonts();

          // Combine all custom font names and normalize them
          const allCustomFontNames = new Set<string>();
          cdnFonts.forEach((font) => {
            const normalizedName = normalizeFontName(font.name);
            allCustomFontNames.add(normalizedName);
          });
          localFonts.forEach((font) => {
            const normalizedName = normalizeFontName(font.name);
            allCustomFontNames.add(normalizedName);
          });

          // Build a map: baseFontName -> Set of weights
          const fontFamilyMap = new Map<string, Set<string>>();
          // Build a map: fontName -> detected weight
          const nameToWeightMap = new Map<string, string>();

          // Process fonts and detect weights
          for (const customFontName of Array.from(allCustomFontNames)) {
            const baseName = extractBaseFontName(customFontName);

            // Check if this font is italic - if so, skip adding it to weights
            const fontNameLower = customFontName.toLowerCase();
            const isItalic = isItalicFontName(customFontName);
            // Also check metadata
            const localFont = localFonts.find((f) => f.name === customFontName);
            const isItalicFromMetadata = localFont?.style && localFont.style.toLowerCase().includes("italic");
            // Check if this font is condensed - if so, skip adding it to weights
            const isCondensed = isCondensedFontNameBasic(customFontName);

            // Skip italic and condensed fonts - they should not appear in weights, only in styles
            if (isItalic || isItalicFromMetadata || isCondensed) {
              // Still store the weight mapping for reference, but don't add to weights list
              let weight: string | null = null;
              if (localFont?.weight) {
                weight = normalizeWeightName(localFont.weight);
              }
              if (!weight) {
                weight = extractWeightFromItalicFontName(customFontName) || extractWeightFromFontName(customFontName);
                if (weight) {
                  weight = normalizeWeightName(weight);
                }
              }
              if (weight) {
                nameToWeightMap.set(customFontName, weight);
              }
              continue; // Skip adding to fontFamilyMap (weights)
            }

            if (!fontFamilyMap.has(baseName)) {
              fontFamilyMap.set(baseName, new Set());
            }

            // Prioritize weight extraction from font name over stored metadata
            let weight: string | null = null;

            // First try to extract weight from font name
            const extractedWeightFromName = extractWeightFromFontName(customFontName);
            if (extractedWeightFromName) {
              weight = normalizeWeightName(extractedWeightFromName);
            } else if (localFont?.weight) {
              // Fallback to stored metadata if name doesn't contain a weight
              weight = normalizeWeightName(localFont.weight);
            }

            // Only add weight if we found one - don't add a default "Normal" if no weight is detected
            // This prevents adding non-existent weights to the font family
            if (weight) {
              fontFamilyMap.get(baseName)!.add(weight);
              nameToWeightMap.set(customFontName, weight);
            } else {
              // Still store the font name mapping even if we couldn't detect weight
              // This allows the font to be used, but it won't appear in the weights dropdown
              nameToWeightMap.set(customFontName, "Unknown");
            }
          }

          setCustomFontsMap(fontFamilyMap);
          setFontNameToWeightMap(nameToWeightMap);

          // Update available styles after fonts are reloaded
          const baseName = extractBaseFontName(fontName);
          const styles = await getAvailableStyles(baseName);
          setAvailableItalicFonts(styles.italicFonts);
          setAvailableCondensedFonts(styles.condensedFonts);
          setHasUnderline(styles.hasUnderline);

          // Automatically select the uploaded font
          // Check if it's an italic font
          const fontNameLower = fontName.toLowerCase();
          const isItalic = isItalicFontName(fontName);

          // Also check metadata
          const localFont = localFonts.find((f) => f.name === fontName);
          const isItalicFromMetadata = localFont?.style && localFont.style.toLowerCase().includes("italic");

          if (isItalic || isItalicFromMetadata) {
            // For italic fonts, select the font directly (it will appear in styles)
            if (setSelectedFont && typeof setSelectedFont === "function") {
              setSelectedFont(fontName);
            }
            updateShapesProps({
              font: fontName,
              fontWeight: undefined,
              italic: "not-italic",
            });
          } else {
            // For non-italic fonts, select the base name and apply the weight
            if (setSelectedFont && typeof setSelectedFont === "function") {
              setSelectedFont(baseName);
            }
            // Get the detected weight
            const detectedWeight = nameToWeightMap.get(fontName) || "Normal";
            // Find the font file that matches this weight
            const allCustomFontNames = new Set<string>();
            cdnFonts.forEach((font) => {
              const normalizedName = normalizeFontName(font.name);
              allCustomFontNames.add(normalizedName);
            });
            localFonts.forEach((font) => {
              const normalizedName = normalizeFontName(font.name);
              allCustomFontNames.add(normalizedName);
            });

            let matchingFont = fontName; // Default to the uploaded font name
            for (const customFontName of Array.from(allCustomFontNames)) {
              const customBaseName = extractBaseFontName(customFontName);
              if (customBaseName === baseName) {
                const customFontNameLower = customFontName.toLowerCase();
                const isCustomItalic =
                  customFontNameLower.includes("italic") ||
                  customFontNameLower.includes("it") ||
                  customFontNameLower.includes("bolditalic") ||
                  customFontNameLower.includes("regularitalic") ||
                  customFontNameLower.includes("normalitalic") ||
                  customFontNameLower.includes("mediumitalic") ||
                  customFontNameLower.includes("lightitalic") ||
                  customFontNameLower.includes("thinitalic") ||
                  customFontNameLower.includes("extralightitalic") ||
                  customFontNameLower.includes("extra-lightitalic") ||
                  customFontNameLower.includes("semibolditalic") ||
                  customFontNameLower.includes("semi-bolditalic") ||
                  customFontNameLower.includes("ultrabolditalic") ||
                  customFontNameLower.includes("ultra-bolditalic") ||
                  customFontNameLower.includes("blackitalic") ||
                  customFontNameLower.includes("heavyitalic");
                if (!isCustomItalic) {
                  const customFontWeight = nameToWeightMap.get(customFontName);
                  if (customFontWeight === detectedWeight) {
                    matchingFont = customFontName;
                    break;
                  }
                }
              }
            }

            updateShapesProps({
              font: matchingFont,
              fontWeight: undefined,
              italic: "not-italic",
            });
          }
        } catch (reloadError) {
          console.error("Error reloading custom fonts after upload:", reloadError);
          // Fallback: update map manually
          setCustomFontsMap((prevMap) => {
            const newMap = new Map(prevMap);
            const baseName = extractBaseFontName(fontName);
            const weight = extractWeightFromFontName(fontName);

            if (!newMap.has(baseName)) {
              newMap.set(baseName, new Set());
            }

            if (weight) {
              const normalizedWeight = normalizeWeightName(weight);
              newMap.get(baseName)!.add(normalizedWeight);
            } else {
              newMap.get(baseName)!.add("Normal");
            }

            return newMap;
          });

          // Still try to select the font even if reload failed
          const baseName = extractBaseFontName(fontName);
          if (setSelectedFont && typeof setSelectedFont === "function") {
            setSelectedFont(baseName);
          }
          updateShapesProps({
            font: fontName,
            fontWeight: undefined,
            italic: "not-italic",
          });
        }

        console.log(`✅ Custom font "${fontName}" uploaded to S3 CDN and available across all devices`);

        // Success snackbar intentionally omitted — the queue dialog that
        // opens right after already signals a successful upload.
      } catch (error) {
        console.error("Failed to upload and store font:", error);
        setFontLoadError(true);
        setFontsLoading(false);

        runSnackBar({
          msg: `Failed to upload font "${fontName}". Please try again.`,
          type: "error",
          vertical: "top",
          horizontal: "center",
        });
      }
    },
    [fonts, setSelectedFont, runSnackBar]
  );

  useEffect(() => {
    if (fontSize !== "Mixed" && fontScale !== "Mixed" && fontSize !== "" && fontScale !== "") {
      const numSize = typeof fontSize === "string" ? parseFloat(fontSize) : fontSize;
      const numScale = typeof fontScale === "string" ? parseFloat(fontScale) : fontScale;

      if (!isNaN(numSize) && !isNaN(numScale) && numSize > 0 && numScale > 0) {
        setDisplayFontSize((Math.round(numSize * numScale * 10) / 10).toString());
      } else {
        setDisplayFontSize("");
      }
    } else if (fontSize === "Mixed" || fontScale === "Mixed") {
      setDisplayFontSize("Mixed");
    } else {
      setDisplayFontSize("");
    }
  }, [fontSize, fontScale]);

  const getFontSizeDisplayValue = useCallback(() => {
    if (!selectedShapes || selectedShapes.length === 0) return "";

    const textShapes = selectedShapes.filter((shape) => shape?.props?.text);
    if (textShapes.length === 0) return "";

    const firstSize = textShapes[0]?.props?.size;
    const isMixed = textShapes.some((shape) => shape?.props?.size !== firstSize);

    return isMixed ? "Mixed" : firstSize?.toString() || "";
  }, [selectedShapes]);

  const getFontScaleDisplayValue = useCallback(() => {
    if (!selectedShapes || selectedShapes.length === 0) return "";

    const textShapes = selectedShapes.filter((shape) => shape?.props?.text);
    if (textShapes.length === 0) return "";

    const firstScale = textShapes[0]?.props?.scale;
    const isMixed = textShapes.some((shape) => shape?.props?.scale !== firstScale);

    return isMixed ? "Mixed" : firstScale?.toString() || "";
  }, [selectedShapes]);

  const getFontWeightDisplayValue = useCallback(() => {
    if (!selectedShapes || selectedShapes.length === 0) return "";

    const textShapes = selectedShapes.filter((shape) => shape?.props?.text);
    if (textShapes.length === 0) return "";

    const firstFont = textShapes[0]?.props?.font;
    const firstWeight = textShapes[0]?.props?.fontWeight;
    const isMixed = textShapes.some((shape) => {
      const shapeFont = shape?.props?.font;
      const shapeWeight = shape?.props?.fontWeight;
      return shapeFont !== firstFont || shapeWeight !== firstWeight;
    });

    if (isMixed) return "Mixed";

    // For custom fonts, use detected weight from metadata or name
    if (firstFont) {
      const baseName = extractBaseFontName(firstFont);
      if (customFontsMap.has(baseName)) {
        // Try to get weight from the map first (uses metadata when available)
        const detectedWeight = fontNameToWeightMap.get(firstFont);
        if (detectedWeight) {
          return detectedWeight;
        }

        // Fallback: extract from name
        const weight = extractWeightFromFontName(firstFont);
        if (weight) {
          const normalizedWeight = normalizeWeightName(weight);
          return normalizedWeight;
        } else {
          // If no weight detected in font name, check if it's in the available weights
          const availableWeights = customFontsMap.get(baseName);
          if (availableWeights && availableWeights.size > 0) {
            // Default to first available weight or "Normal"
            return availableWeights.has("Normal") ? "Normal" : Array.from(availableWeights)[0];
          }
          return "Normal";
        }
      }
    }

    // For regular fonts, use the fontWeight property
    const weightName = getFontWeightName(firstWeight);
    return weightName;
  }, [selectedShapes, customFontsMap]);

  useEffect(() => {
    const sizeDisplayValue = getFontSizeDisplayValue();
    const scaleDisplayValue = getFontScaleDisplayValue();

    setFontSize(sizeDisplayValue || "");
    setFontScale(scaleDisplayValue || 1);
  }, [selectedShapes, getFontSizeDisplayValue, getFontScaleDisplayValue]);

  const toggleProp = useCallback(
    (propName: string, trueValue: any, falseValue: any) => {
      if (selectedShape?.props && propName) {
        const currentValue = selectedShape.props[propName];
        const newValue = currentValue === trueValue ? falseValue : trueValue;
        updateShapesProps({ [propName]: newValue });
      }
    },
    [selectedShape, updateShapesProps]
  );

  const handleAlignmentChange = useCallback(
    (alignment: any) => {
      if (alignment) {
        updateShapesProps({ textAlign: alignment });
      }
    },
    [updateShapesProps]
  );

  const handleVerticalAlignmentChange = useCallback(
    (alignment: any) => {
      if (alignment) {
        updateShapesProps({ verticalAlign: alignment });
      }
    },
    [updateShapesProps]
  );

  // Apply a brand font preset to selected text shapes
  const handleApplyFontPreset = useCallback(
    async (preset: BrandKitFontPreset) => {
      setPresetDropdownOpen(false);
      setSelectedPresetId(preset.id);
      // Clear any lingering preview snapshot so unmount cleanup doesn't revert
      setPreviewSnapshot(null);
      previewSnapshotRef.current = null;
      selectionCommittedRef.current = true;
      if (!editor) return;

      // Parse italic/underline flags from fontVariant (e.g. "Roboto-Bold:i:u")
      const variantParts = preset.fontVariant.split(":");
      const baseVariant = variantParts[0];
      const applyItalic = variantParts.includes("i");
      const applyUnderline = variantParts.includes("u");

      // Check if this is a custom CDN font
      const cdnFonts = await s3CustomFontService.getLocalFontRegistry();
      const isCustom = cdnFonts.some((f) => f.name === baseVariant);

      if (isCustom) {
        // Load custom font file if needed
        const existing = Array.from(document.fonts).find((f) => f.family.toLowerCase() === baseVariant.toLowerCase());
        if (!existing || existing.status !== "loaded") {
          const fontData = cdnFonts.find((f) => f.name === baseVariant);
          if (fontData) {
            try {
              const fontFace = new FontFace(baseVariant, `url(${fontData.s3Url})`);
              await fontFace.load();
              document.fonts.add(fontFace);
            } catch (err) {
              console.error(`Failed to load font "${baseVariant}":`, err);
            }
          }
        }
        updateShapesProps({
          font: baseVariant,
          size: preset.fontSize,
          lineHeight: preset.lineHeight,
          letterSpacing: preset.letterSpacing,
          italic: applyItalic ? "italic" : "not-italic",
          underline: applyUnderline ? "underline" : "none",
        });
      } else {
        // Standard font: use family name + CSS fontWeight
        const suffix = baseVariant.slice(preset.fontFamily.length).replace(/^[-_]/, "").toLowerCase();
        let fontWeight = 400;
        if (suffix === "light") fontWeight = 300;
        else if (suffix === "semibold" || suffix === "semi-bold") fontWeight = 600;
        else if (suffix === "bold") fontWeight = 700;

        updateShapesProps({
          font: preset.fontFamily,
          fontWeight,
          size: preset.fontSize,
          lineHeight: preset.lineHeight,
          letterSpacing: preset.letterSpacing,
          italic: applyItalic ? "italic" : "not-italic",
          underline: applyUnderline ? "underline" : "none",
        });
      }
    },
    [editor, updateShapesProps]
  );

  // Close preset dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (presetDropdownRef.current && !presetDropdownRef.current.contains(e.target as Node)) {
        setPresetDropdownOpen(false);
      }
    };
    if (presetDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [presetDropdownOpen]);

  // Auto-detect matching preset when shape selection changes
  useEffect(() => {
    if (!selectedShapes || selectedShapes.length === 0 || fontPresets.length === 0) {
      setSelectedPresetId(null);
      return;
    }
    const shape = selectedShapes.find((s: any) => s?.type === "customText");
    if (!shape) {
      setSelectedPresetId(null);
      return;
    }
    const { font, size, lineHeight, letterSpacing } = shape.props;
    const match = fontPresets.find(
      (p: BrandKitFontPreset) =>
        (p.fontVariant === font || p.fontFamily === font || p.fontVariant.split(":")[0] === font) &&
        p.fontSize === size &&
        p.lineHeight === lineHeight &&
        p.letterSpacing === letterSpacing
    );
    setSelectedPresetId(match ? match.id : null);
  }, [selectedShapes, fontPresets]);

  // Sync first font preset as default font for new text shapes
  useEffect(() => {
    if (fontPresets.length > 0) {
      const p = fontPresets[0];
      saveLastSelectedFont(p.fontFamily);
      try {
        localStorage.setItem(
          "brandDefaultFontPreset",
          JSON.stringify({
            fontFamily: p.fontFamily,
            fontVariant: p.fontVariant,
            fontSize: p.fontSize,
            lineHeight: p.lineHeight,
            letterSpacing: p.letterSpacing,
          })
        );
      } catch {
        // ignore localStorage errors
      }
    } else {
      try {
        localStorage.removeItem("brandDefaultFontPreset");
      } catch {
        // ignore localStorage errors
      }
    }
  }, [fontPresets, saveLastSelectedFont]);

  return (
    <div className={classes.textParametersRoot}>
      {selectedShapes && Array.isArray(selectedShapes) && selectedShapes.length > 0 && (
        <>
          <div
            style={{
              paddingLeft: "8px",
              paddingRight: "8px",
              width: "100%",
            }}
          >
            <div
              style={{
                width: "100%",
                height: "1px",
                backgroundColor: "#E2E2FC",
                marginTop: "8px",
                marginBottom: "8px",
              }}
            />
          </div>{" "}
          <div className="px-4">
            <>
              <div className="flex flex-col gap-2">
                <div className="flex flex-col gap-1">
                  <div className="flex justify-between items-center">
                    <Title name={"Font"} />
                    <label ref={brandFontAnchorRef} className="flex items-center justify-between cursor-pointer">
                      {fontUploading ? (
                        <span className="flex items-center gap-1 text-[12px] font-bold -mb-0.5 text-gray-400">
                          <CircularProgress size={10} style={{ color: "#9ca3af" }} />
                          Uploading...
                        </span>
                      ) : (
                        <p className="text-[12px] font-bold -mb-0.5 text-red-500">Upload font</p>
                      )}
                      <input
                        type="file"
                        accept=".ttf,.otf,.woff,.woff2"
                        multiple
                        style={{ display: "none" }}
                        disabled={fontUploading}
                        onChange={async (e) => {
                          const files = Array.from(e.target.files || []);
                          if (files.length === 0) return;
                          setFontUploading(true);
                          const successful: string[] = [];
                          try {
                            // Upload all files in sequence. Each file is
                            // registered in BOTH systems so the font appears
                            // in the font dropdown (s3CustomFontService) and
                            // in the Brand Kit assets list (uploadBrandKitAsset).
                            for (const file of files) {
                              const fontName = file.name.replace(/\.[^.]+$/, "");
                              try {
                                // Immediate FontFace preview (best-effort).
                                try {
                                  const previewDataUrl = await new Promise<string>((resolve, reject) => {
                                    const reader = new FileReader();
                                    reader.onload = () => resolve(reader.result as string);
                                    reader.onerror = () => reject(new Error("Failed to read file"));
                                    reader.readAsDataURL(file);
                                  });
                                  const fontFace = new FontFace(fontName, `url(${previewDataUrl})`);
                                  try {
                                    await fontFace.load();
                                    document.fonts.add(fontFace);
                                  } catch {
                                    /* preview failed — continue */
                                  }
                                } catch {
                                  /* read failed — still try to upload */
                                }

                                // 1. Upload to font CDN so it appears in dropdowns.
                                await s3CustomFontService.uploadFontToCDN(file, fontName);
                                // 2. Register as a Brand Kit asset.
                                try {
                                  await uploadBrandKitAsset(file, "font");
                                } catch (assetErr) {
                                  console.error(`Brand kit asset save failed for "${fontName}":`, assetErr);
                                }
                                successful.push(fontName);
                              } catch (err) {
                                console.error(`Upload failed for "${fontName}":`, err);
                                runSnackBar({
                                  msg: `Failed to upload "${fontName}".`,
                                  type: "error",
                                  vertical: "top",
                                  horizontal: "center",
                                });
                              }
                            }
                            if (successful.length > 0) {
                              // Refresh both sources so every consumer sees
                              // the new fonts.
                              s3CustomFontService.clearCache();
                              await loadBrandKit();
                              window.dispatchEvent(new Event("custom-fonts-updated"));
                              // Success snackbar intentionally omitted — the
                              // queue dialog that opens right after already
                              // signals a successful upload.
                              // Group uploaded fonts by detected family and show
                              // a queue dialog — each family can be added or
                              // skipped individually.
                              const groups = new Map<string, string[]>();
                              successful.forEach((fontName) => {
                                const base = extractBaseFontName(fontName);
                                if (!groups.has(base)) groups.set(base, []);
                                groups.get(base)!.push(fontName);
                              });
                              const entries: BrandFontsQueueFamily[] = Array.from(groups.entries()).map(
                                ([baseName, variantNames]) => ({
                                  baseName,
                                  variantNames,
                                })
                              );
                              if (entries.length > 0) {
                                setBrandFontQueue(entries);
                                setBrandFontQueueOpen(true);
                              }
                            }
                          } finally {
                            setFontUploading(false);
                          }
                          e.target.value = "";
                        }}
                      />
                    </label>
                  </div>
                  <Tooltip
                    title="Select font"
                    arrow
                    placement="top"
                    classes={{ tooltip: classesTooltip.tooltip, arrow: classesTooltip.arrow }}
                  >
                    <div ref={fontDropdownWrapperRef} className={classes.fontDropdownWrapper}>
                      <button
                        className={classes.fontDropdownBtn}
                        disabled={fontsLoading || fontLoadError}
                        onClick={() => {
                          const opening = !fontSelectorOpen;
                          setFontSelectorOpen(opening);
                          setFontDropdownOpen(opening);
                          if (opening) {
                            setFontSearchTerm("");
                            setFontSizeOpen(false);
                            setTimeout(() => fontSearchInputRef.current?.focus(), 50);
                          }
                        }}
                      >
                        <span
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            flex: 1,
                            textAlign: "left",
                          }}
                        >
                          {fontsLoading
                            ? "Loading fonts..."
                            : fontLoadError
                            ? "Error loading fonts"
                            : getFontButtonLabel() || "Select font"}
                        </span>
                        {(() => {
                          const currentVal = getFontDisplayValue();
                          const currentBaseName = currentVal ? extractBaseFontName(currentVal) : "";
                          // A font only counts as "Brand" when it has a
                          // Brand Kit PRESET — just being stored as a Brand
                          // Kit font asset (because it was uploaded) isn't
                          // enough. `brandFontNames` still tracks the wider
                          // upload set for other UX but the badge follows
                          // the same rule as the dropdown grouping.
                          const isBrandFont = currentBaseName && brandPresetFamilies.has(currentBaseName);
                          // "Custom" = uploaded font that isn't (yet) part of
                          // the Brand Kit presets. We only show it when it's
                          // NOT a brand font, so the two badges never coexist.
                          const isCustomOnly = !isBrandFont && currentBaseName && customFontsMap.has(currentBaseName);
                          const badgeLabel = isBrandFont ? "Brand" : isCustomOnly ? "Custom" : null;
                          return badgeLabel ? (
                            <span
                              style={{
                                fontSize: "9px",
                                color: "#999",
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                padding: "1px 5px",
                                lineHeight: "14px",
                                whiteSpace: "nowrap",
                                fontFamily: "sans-serif",
                                marginRight: "8px",
                                flexShrink: 0,
                              }}
                            >
                              {badgeLabel}
                            </span>
                          ) : null;
                        })()}
                        {fontsLoading ? (
                          <CircularProgress size={14} style={{ color: "#707087", flexShrink: 0 }} />
                        ) : (
                          <DropdownChevronIcon
                            style={{
                              transition: "transform 0.15s",
                              transform: fontSelectorOpen ? "rotate(180deg)" : "rotate(0deg)",
                              flexShrink: 0,
                              position: "static",
                            }}
                          />
                        )}
                      </button>

                      {fontSelectorOpen && (
                        <div className={classes.fontDropdownList}>
                          <div
                            style={{
                              padding: "6px",
                              position: "sticky",
                              top: 0,
                              background: "white",
                              zIndex: 1,
                              borderRadius: "8px 8px 0 0",
                            }}
                          >
                            <input
                              ref={fontSearchInputRef}
                              className={classes.fontDropdownSearch}
                              type="text"
                              placeholder="Search fonts..."
                              value={fontSearchTerm}
                              onChange={(e) => setFontSearchTerm(e.target.value)}
                              onKeyDown={(e) => e.stopPropagation()}
                            />
                          </div>

                          {/* Brand Fonts section */}
                          {brandFonts.length > 0 && (
                            <>
                              <div
                                style={{
                                  padding: "4px 12px",
                                  fontSize: 9,
                                  fontWeight: 600,
                                  color: "#798498",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.5px",
                                  fontFamily: "Inter, sans-serif",
                                }}
                              >
                                Brand Fonts
                              </div>
                              {brandFonts.map((option) => {
                                const baseName = extractBaseFontName(option);
                                const presetLabel = brandPresetLabelMap.get(baseName) || option;
                                return (
                                  <div
                                    key={`brand-${option}`}
                                    className={classes.fontDropdownItem}
                                    style={{ fontFamily: `"${option}", sans-serif` }}
                                    onPointerEnter={() => {
                                      applyFontPreview(option);
                                      setHoveredMenuItem(`font:${option}`);
                                    }}
                                    onPointerLeave={() => setHoveredMenuItem(null)}
                                    onClick={() => {
                                      selectionCommittedRef.current = true;
                                      setPreviewSnapshot(null);
                                      handleFontChange(option);
                                      setFontToDelete(null);
                                      setFontSelectorOpen(false);
                                      setFontDropdownOpen(false);
                                    }}
                                  >
                                    <span
                                      style={{
                                        flex: 1,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        paddingRight: "8px",
                                      }}
                                    >
                                      {presetLabel}
                                    </span>
                                    <span
                                      style={{
                                        fontSize: "9px",
                                        color: "#999",
                                        border: "1px solid #ccc",
                                        borderRadius: "4px",
                                        padding: "1px 5px",
                                        marginRight: "6px",
                                        lineHeight: "14px",
                                        whiteSpace: "nowrap",
                                        fontFamily: "sans-serif",
                                        flexShrink: 0,
                                      }}
                                    >
                                      Brand
                                    </span>
                                  </div>
                                );
                              })}
                              {(customFonts.length > 0 || otherFonts.length > 0) && (
                                <div style={{ height: 1, background: "#E2E2FC", margin: "4px 8px" }} />
                              )}
                            </>
                          )}
                          {/* Custom Fonts (uploaded but not yet in Brand Kit)
                              + all other fonts. Both sections share the
                              same row renderer — the only difference is the
                              "Custom Fonts" group header + "Custom" badge
                              rendered for rows in the Custom Fonts list. */}
                          {(() => {
                            const renderFontRow = (option: string, showCustomBadge: boolean) => {
                              const baseName = extractBaseFontName(option);
                              const isCustomFont = customFontsMap.has(baseName);
                              const isConfirmingDelete = fontToDelete === option;

                              const handleDeleteClick = (e: React.MouseEvent) => {
                                e.stopPropagation();
                                if (isConfirmingDelete) {
                                  setFontToDelete(null);
                                } else {
                                  setFontToDelete(option);
                                }
                              };

                              const handleConfirmDelete = async (e: React.MouseEvent) => {
                                e.stopPropagation();
                                setDeletingFont(baseName);
                                setFontToDelete(null);
                                showNotification(`🔄 Deleting font "${baseName}"...`, "info");

                                try {
                                  const cdnFonts = await s3CustomFontService.getLocalFontRegistry();
                                  const localFonts = await customFontStorage.getCustomFonts();
                                  const normalizedToOriginal = new Map<string, string>();
                                  cdnFonts.forEach((f) => {
                                    const normalized = normalizeFontName(f.name);
                                    normalizedToOriginal.set(normalized, f.name);
                                  });
                                  localFonts.forEach((f) => {
                                    const normalized = normalizeFontName(f.name);
                                    normalizedToOriginal.set(normalized, f.name);
                                  });
                                  const allCustomFontsNormalized = Array.from(normalizedToOriginal.keys());

                                  const fontsToDeleteNormalized = allCustomFontsNormalized.filter((fontName) => {
                                    const fontBaseName = extractBaseFontName(fontName);
                                    return fontBaseName === baseName;
                                  });

                                  const fontsToDelete = fontsToDeleteNormalized
                                    .map((normalized) => normalizedToOriginal.get(normalized))
                                    .filter((name): name is string => name !== undefined);

                                  for (const fontName of fontsToDelete) {
                                    try {
                                      await s3CustomFontService.removeFontFromRegistry(fontName);
                                    } catch (error) {
                                      console.warn(`Failed to remove ${fontName} from S3 registry:`, error);
                                    }
                                    try {
                                      await customFontStorage.deleteCustomFont(fontName);
                                    } catch (error) {
                                      console.warn(`Failed to remove ${fontName} from local storage:`, error);
                                    }
                                    const existingFont = Array.from(document.fonts).find(
                                      (font) => font.family.toLowerCase() === fontName.toLowerCase()
                                    );
                                    if (existingFont) {
                                      document.fonts.delete(existingFont);
                                    }
                                  }

                                  const updatedCdnFonts = await s3CustomFontService.getLocalFontRegistry();
                                  const updatedLocalFonts = await customFontStorage.getCustomFonts();
                                  const updatedAllCustomFontNames = new Set<string>();
                                  updatedCdnFonts.forEach((font) => updatedAllCustomFontNames.add(font.name));
                                  updatedLocalFonts.forEach((font) => updatedAllCustomFontNames.add(font.name));

                                  const updatedFontFamilyMap = new Map<string, Set<string>>();
                                  const updatedNameToWeightMap = new Map<string, string>();

                                  for (const fontName of Array.from(updatedAllCustomFontNames)) {
                                    const fontBaseName = extractBaseFontName(fontName);
                                    if (!updatedFontFamilyMap.has(fontBaseName)) {
                                      updatedFontFamilyMap.set(fontBaseName, new Set());
                                    }
                                    let weight: string | null = null;
                                    const extractedWeightFromName = extractWeightFromFontName(fontName);
                                    if (extractedWeightFromName) {
                                      weight = normalizeWeightName(extractedWeightFromName);
                                    } else {
                                      const localFont = updatedLocalFonts.find((f) => f.name === fontName);
                                      if (localFont?.weight) {
                                        weight = normalizeWeightName(localFont.weight);
                                      }
                                    }
                                    if (weight) {
                                      updatedFontFamilyMap.get(fontBaseName)!.add(weight);
                                      updatedNameToWeightMap.set(fontName, weight);
                                    } else {
                                      updatedNameToWeightMap.set(fontName, "Unknown");
                                    }
                                  }

                                  setCustomFontsMap(updatedFontFamilyMap);
                                  setFontNameToWeightMap(updatedNameToWeightMap);
                                  setDeletingFont(null);

                                  if (Array.isArray(fonts)) {
                                    fontsToDelete.forEach((fontName) => {
                                      const index = fonts.indexOf(fontName);
                                      if (index > -1) {
                                        fonts.splice(index, 1);
                                      }
                                    });
                                  }

                                  const currentFont = selectedShape?.props?.font;
                                  if (currentFont) {
                                    const currentFontBaseName = extractBaseFontName(currentFont);
                                    if (currentFontBaseName === baseName) {
                                      if (setSelectedFont && typeof setSelectedFont === "function") {
                                        setSelectedFont("Roboto");
                                      }
                                      updateShapesProps({ font: "Roboto" });
                                    }
                                  }

                                  showNotification(`✅ Font "${baseName}" deleted successfully`, "success");
                                } catch (error) {
                                  console.error("Error deleting font:", error);
                                  showNotification(`❌ Failed to delete font "${baseName}"`, "error");
                                  setDeletingFont(null);
                                }
                              };

                              const handleCancelDelete = (e: React.MouseEvent) => {
                                e.stopPropagation();
                                setFontToDelete(null);
                              };

                              return (
                                <div
                                  key={option}
                                  className={classes.fontDropdownItem}
                                  style={{ fontFamily: `"${option}", sans-serif` }}
                                  onPointerEnter={() => {
                                    if (!isConfirmingDelete && !deletingFont) {
                                      applyFontPreview(option);
                                      setHoveredMenuItem(`font:${option}`);
                                    }
                                  }}
                                  onPointerLeave={() => {
                                    if (!isConfirmingDelete && !deletingFont) {
                                      setHoveredMenuItem(null);
                                    }
                                  }}
                                  onClick={() => {
                                    if (!isConfirmingDelete && !deletingFont) {
                                      selectionCommittedRef.current = true;
                                      setPreviewSnapshot(null);
                                      handleFontChange(option);
                                      setFontToDelete(null);
                                      setFontSelectorOpen(false);
                                      setFontDropdownOpen(false);
                                    }
                                  }}
                                >
                                  <span
                                    style={{
                                      flex: 1,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      paddingRight: "8px",
                                    }}
                                  >
                                    {option}
                                  </span>
                                  {/* Brand badge removed — brand fonts now have their own section */}
                                  {showCustomBadge && (
                                    <span
                                      style={{
                                        fontSize: "9px",
                                        color: "#999",
                                        border: "1px solid #ccc",
                                        borderRadius: "4px",
                                        padding: "1px 5px",
                                        marginRight: "6px",
                                        lineHeight: "14px",
                                        whiteSpace: "nowrap",
                                        fontFamily: "sans-serif",
                                        flexShrink: 0,
                                      }}
                                    >
                                      Custom
                                    </span>
                                  )}
                                  {isCustomFont && (
                                    <div className="font-delete-container" onClick={(e) => e.stopPropagation()}>
                                      {deletingFont === baseName ? (
                                        <div className="font-delete-loading">
                                          <CircularProgress size={14} style={{ color: "#999" }} />
                                          <span className="font-delete-loading-text">Deleting...</span>
                                        </div>
                                      ) : isConfirmingDelete ? (
                                        <div className="font-delete-confirm">
                                          <span className="font-delete-confirm-text">Delete?</span>
                                          <button onClick={handleConfirmDelete} className="font-delete-button-yes">
                                            Yes
                                          </button>
                                          <button onClick={handleCancelDelete} className="font-delete-button-no">
                                            No
                                          </button>
                                        </div>
                                      ) : (
                                        <DeleteIcon onClick={handleDeleteClick} className="font-delete-icon" />
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            };
                            return (
                              <>
                                {customFonts.length > 0 && (
                                  <>
                                    <div
                                      style={{
                                        padding: "4px 12px",
                                        fontSize: 9,
                                        fontWeight: 600,
                                        color: "#798498",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.5px",
                                        fontFamily: "Inter, sans-serif",
                                      }}
                                    >
                                      Custom Fonts
                                    </div>
                                    {customFonts.map((option) => renderFontRow(option, true))}
                                    {otherFonts.length > 0 && (
                                      <div style={{ height: 1, background: "#E2E2FC", margin: "4px 8px" }} />
                                    )}
                                  </>
                                )}
                                {otherFonts.map((option) => renderFontRow(option, false))}
                              </>
                            );
                          })()}

                          {filteredFonts.length === 0 && (
                            <div style={{ padding: "8px 12px", fontSize: 10, color: "#A0A0B8", textAlign: "center" }}>
                              {fontLoadError ? "Error loading fonts" : "No fonts found"}
                            </div>
                          )}
                        </div>
                      )}

                      {fontLoadError && (
                        <div
                          style={{
                            position: "absolute",
                            top: "100%",
                            left: 0,
                            right: 0,
                            fontSize: "10px",
                            color: "#f44336",
                            marginTop: "4px",
                          }}
                        >
                          Failed to load fonts. Using fallback.
                        </div>
                      )}
                    </div>
                  </Tooltip>
                </div>
                <div className="flex gap-2">
                  <div style={{ cursor: "pointer", flex: 1 }}>
                    <Tooltip
                      title="Font weight and style"
                      arrow
                      placement="top"
                      classes={{ tooltip: classesTooltip.tooltip, arrow: classesTooltip.arrow }}
                    >
                      <div>
                        <div
                          style={{
                            width: "100%",
                            height: "36px",
                            borderRadius: "8px",
                            border: "1px solid #E1E6EB",
                            fontSize: "10px",
                            cursor: "pointer",
                            padding: "6px 10px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                          onPointerDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            selectionCommittedRef.current = false;
                            setPreviewSnapshot({
                              font: selectedShape?.props?.font,
                              fontWeight: selectedShape?.props?.fontWeight,
                              italic: selectedShape?.props?.italic,
                            });
                            setWeightMenuAnchor(e.currentTarget as HTMLElement);
                          }}
                        >
                          <span style={{ fontSize: "10px" }}>
                            {(() => {
                              const currentFont = selectedShape?.props?.font || "";
                              const baseName = extractBaseFontName(currentFont);
                              const isCustom = customFontsMap.has(baseName);
                              const fontNameLower = currentFont.toLowerCase();
                              const isCondensed =
                                fontNameLower.includes("condensed") ||
                                fontNameLower.includes("cond") ||
                                fontNameLower.includes("cn") ||
                                fontNameLower.includes("semicn") ||
                                fontNameLower.includes("semicond");
                              const isItalic =
                                fontNameLower.includes("italic") ||
                                fontNameLower.includes(" italic") ||
                                fontNameLower.endsWith("it") ||
                                fontNameLower.endsWith("-it") ||
                                fontNameLower.endsWith("_it") ||
                                fontNameLower.includes(" oblique") ||
                                fontNameLower.endsWith("oblique");

                              const currentFontDisplay = getFontWeightDisplayValue();

                              if (isCustom) {
                                const weight =
                                  extractWeightFromItalicFontName(currentFont) ||
                                  extractWeightFromCondensedFontName(currentFont) ||
                                  extractWeightFromFontName(currentFont) ||
                                  currentFontDisplay ||
                                  "Regular";
                                const normalizedWeight = normalizeWeightName(weight || "Regular");
                                const weightLabel =
                                  normalizedWeight === "Normal" || normalizedWeight === "Regular"
                                    ? "Regular"
                                    : normalizedWeight;

                                let condensedType = "";
                                if (isCondensed) {
                                  if (
                                    fontNameLower.includes("extracondensed") ||
                                    fontNameLower.includes("extra-condensed")
                                  ) {
                                    condensedType = "Extra Condensed";
                                  } else if (
                                    fontNameLower.includes("ultracondensed") ||
                                    fontNameLower.includes("ultra-condensed")
                                  ) {
                                    condensedType = "Ultra Condensed";
                                  } else {
                                    condensedType = "Condensed";
                                  }
                                }

                                let label = weightLabel;
                                if (isCondensed) label = `${label} ${condensedType}`;
                                if (isItalic) label = `${label} Italic`;
                                return label;
                              } else {
                                if (selectedShape?.props?.italic === "italic") return "Italic";
                              }
                              return (
                                (getFontWeightDisplayValue() === "Normal" ? "Regular" : getFontWeightDisplayValue()) ||
                                ""
                              );
                            })()}
                          </span>
                          <DropdownChevronIcon
                            style={{
                              marginLeft: "8px",
                              position: "static",
                            }}
                          />
                        </div>
                        <Popper open={weightMenuOpen} anchorEl={weightMenuAnchor} placement="bottom-start">
                          <ClickAwayListener
                            mouseEvent="onMouseUp"
                            touchEvent="onTouchEnd"
                            onClickAway={() => closeWeightMenu(false)}
                          >
                            <div
                              ref={popperRef}
                              style={{
                                background: "white",
                                border: "1px solid #E1E6EB",
                                borderRadius: 8,
                                boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                                padding: 0,
                                width: "140px",
                                zIndex: 9999,
                              }}
                            >
                              {fontWeights.map((option) => {
                                const displayLabel = option === "Normal" ? "Regular" : option;
                                const isSelectedWeight = (() => {
                                  const currentFont = selectedShape?.props?.font || "";
                                  const baseName = extractBaseFontName(currentFont);
                                  const isCustom = customFontsMap.has(baseName);
                                  const fontNameLower = currentFont.toLowerCase();
                                  const isCondensedFont =
                                    fontNameLower.includes("condensed") ||
                                    fontNameLower.includes("cond") ||
                                    fontNameLower.includes("cn") ||
                                    fontNameLower.includes("semicn") ||
                                    fontNameLower.includes("semicond");
                                  const isItalicFont =
                                    fontNameLower.includes("italic") ||
                                    fontNameLower.includes(" italic") ||
                                    fontNameLower.endsWith("it") ||
                                    fontNameLower.endsWith("-it") ||
                                    fontNameLower.endsWith("_it") ||
                                    fontNameLower.includes(" oblique") ||
                                    fontNameLower.endsWith("oblique");
                                  // Condensed or italic custom fonts should not map to base weights for highlight
                                  if (isCustom && (isCondensedFont || isItalicFont)) return false;
                                  if (isCustom) {
                                    const w =
                                      extractWeightFromFontName(currentFont) || getFontWeightDisplayValue() || "";
                                    return normalizeWeightName(w || "") === normalizeWeightName(displayLabel || "");
                                  }
                                  if (selectedShape?.props?.italic === "italic") return false;
                                  const weightVal = getFontWeightValue(
                                    displayLabel === "Regular" ? "Normal" : displayLabel
                                  );
                                  if (weightVal !== null && weightVal !== undefined) {
                                    return selectedShape?.props?.fontWeight === weightVal;
                                  }
                                  const w = getFontWeightDisplayValue() || "";
                                  return normalizeWeightName(w || "") === normalizeWeightName(displayLabel || "");
                                })();
                                return (
                                  <div
                                    key={option}
                                    onPointerEnter={() => {
                                      applyWeightPreview(displayLabel);
                                      setHoveredMenuItem(`weight:${displayLabel}`);
                                    }}
                                    onPointerLeave={() => setHoveredMenuItem(null)}
                                    onPointerDown={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      commitWeight(displayLabel);
                                    }}
                                    style={{
                                      padding: "6px 12px",
                                      fontSize: 10,
                                      cursor: "pointer",
                                      backgroundColor:
                                        hoveredMenuItem === `weight:${displayLabel}` || isSelectedWeight
                                          ? "#d3d3f8"
                                          : "transparent",
                                    }}
                                  >
                                    {displayLabel}
                                  </div>
                                );
                              })}

                              <div style={{ height: 1, background: "#E1E6EB", margin: "6px 12px" }} />

                              {availableItalicFonts.map((italicFontName, index) => {
                                const weight =
                                  extractWeightFromItalicFontName(italicFontName) ||
                                  extractWeightFromFontName(italicFontName);
                                const normalizedWeight = weight ? normalizeWeightName(weight) : "Regular";
                                const displayLabel =
                                  normalizedWeight === "Normal" || normalizedWeight === "Regular"
                                    ? "Italic"
                                    : `${normalizedWeight} Italic`;
                                return (
                                  <div
                                    key={`${italicFontName}-${index}`}
                                    onPointerEnter={() => {
                                      applyStylePreview(italicFontName);
                                      setHoveredMenuItem(`italic:${italicFontName}`);
                                    }}
                                    onPointerLeave={() => setHoveredMenuItem(null)}
                                    onPointerDown={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      commitFontChange(italicFontName);
                                    }}
                                    style={{
                                      padding: "6px 12px",
                                      fontSize: 10,
                                      cursor: "pointer",
                                      backgroundColor:
                                        hoveredMenuItem === `italic:${italicFontName}` ||
                                        selectedShape?.props?.font === italicFontName
                                          ? "#d3d3f8"
                                          : "transparent",
                                    }}
                                  >
                                    {displayLabel}
                                  </div>
                                );
                              })}

                              {availableCondensedFonts.map((condensedFontName, index) => {
                                const weight =
                                  extractWeightFromCondensedFontName(condensedFontName) ||
                                  extractWeightFromFontName(condensedFontName);
                                const normalizedWeight = weight ? normalizeWeightName(weight) : "Regular";
                                const fontNameLower = condensedFontName.toLowerCase();
                                const isExtraCondensed =
                                  fontNameLower.includes("extracondensed") || fontNameLower.includes("extra-condensed");
                                const isUltraCondensed =
                                  fontNameLower.includes("ultracondensed") || fontNameLower.includes("ultra-condensed");
                                const hasItalic = fontNameLower.includes("italic") || fontNameLower.includes("it");
                                const condensedType = isExtraCondensed
                                  ? "Extra Condensed"
                                  : isUltraCondensed
                                  ? "Ultra Condensed"
                                  : "Condensed";
                                const label = hasItalic
                                  ? `${
                                      normalizedWeight === "Normal" || normalizedWeight === "Regular"
                                        ? "Regular"
                                        : normalizedWeight
                                    } ${condensedType} Italic`
                                  : `${
                                      normalizedWeight === "Normal" || normalizedWeight === "Regular"
                                        ? "Regular"
                                        : normalizedWeight
                                    } ${condensedType}`;
                                return (
                                  <div
                                    key={`${condensedFontName}-${index}`}
                                    onPointerEnter={() => {
                                      applyStylePreview(condensedFontName);
                                      setHoveredMenuItem(`cond:${condensedFontName}`);
                                    }}
                                    onPointerLeave={() => setHoveredMenuItem(null)}
                                    onPointerDown={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      commitFontChange(condensedFontName);
                                    }}
                                    style={{
                                      padding: "6px 12px",
                                      fontSize: 10,
                                      cursor: "pointer",
                                      backgroundColor:
                                        hoveredMenuItem === `cond:${condensedFontName}` ||
                                        selectedShape?.props?.font === condensedFontName
                                          ? "#d3d3f8"
                                          : "transparent",
                                    }}
                                  >
                                    {label}
                                  </div>
                                );
                              })}

                              {(() => {
                                const currentFont = selectedShape?.props?.font || "";
                                const baseName = extractBaseFontName(currentFont);
                                const isCustom = customFontsMap.has(baseName);
                                if (!isCustom && availableItalicFonts.length === 0) {
                                  return (
                                    <div
                                      onPointerEnter={() => setHoveredMenuItem("italic-toggle")}
                                      onPointerLeave={() => setHoveredMenuItem(null)}
                                      onPointerDown={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        commitItalicToggle();
                                      }}
                                      style={{
                                        padding: "6px 12px",
                                        fontSize: 10,
                                        cursor: "pointer",
                                        backgroundColor:
                                          hoveredMenuItem === "italic-toggle" ||
                                          selectedShape?.props?.italic === "italic"
                                            ? "#d3d3f8"
                                            : "transparent",
                                      }}
                                    >
                                      Italic
                                    </div>
                                  );
                                }
                                return null;
                              })()}

                              {hasUnderline && (
                                <div
                                  onPointerEnter={() => setHoveredMenuItem("underline-toggle")}
                                  onPointerLeave={() => setHoveredMenuItem(null)}
                                  onPointerDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    commitUnderlineToggle();
                                  }}
                                  style={{
                                    padding: "6px 12px",
                                    fontSize: 10,
                                    cursor: "pointer",
                                    backgroundColor:
                                      hoveredMenuItem === "underline-toggle" ||
                                      selectedShape?.props?.underline === "underline"
                                        ? "#d3d3f8"
                                        : "transparent",
                                  }}
                                >
                                  Underline
                                </div>
                              )}
                            </div>
                          </ClickAwayListener>
                        </Popper>
                      </div>
                    </Tooltip>
                  </div>
                  <div className="relative" style={{ flex: 1 }}>
                    <Tooltip
                      title="Font size"
                      arrow
                      placement="top"
                      classes={{ tooltip: classesTooltip.tooltip, arrow: classesTooltip.arrow }}
                    >
                      <div style={{ position: "relative", width: "100%", display: "flex", alignItems: "center" }}>
                        <Autocomplete
                          freeSolo
                          disableClearable
                          open={fontSizeOpen}
                          onOpen={() => {
                            setFontSizeOpen(true);
                            setFontSelectorOpen(false); // Close font selector when font size is opened
                          }}
                          onClose={() => setFontSizeOpen(false)}
                          options={[
                            "8",
                            "9",
                            "10",
                            "11",
                            "12",
                            "14",
                            "16",
                            "18",
                            "20",
                            "22",
                            "24",
                            "26",
                            "28",
                            "36",
                            "48",
                            "72",
                          ]}
                          value={displayFontSize}
                          ListboxProps={{
                            style: {
                              maxHeight: "150px",
                              fontSize: "12px",
                            },
                          }}
                          style={{ width: "100%", height: "36px" }}
                          classes={{
                            root: classes.textParametersRoot,
                            option: classes.autocompleteOption,
                          }}
                          onChange={(event, newValue) => {
                            if (newValue && newValue !== "Mixed") {
                              setDisplayFontSize(newValue);
                              const parsedValue = parseFloat(newValue);
                              const currentFontScale =
                                fontScale && fontScale !== "Mixed" && !isNaN(Number(fontScale)) ? Number(fontScale) : 1;
                              if (!isNaN(parsedValue) && currentFontScale > 0) {
                                const scaledValue = parsedValue / currentFontScale;
                                setFontSize(scaledValue);
                                updateShapesProps({ size: scaledValue });
                              }
                            }
                          }}
                          onInputChange={(event, newInputValue, reason) => {
                            // Only react to user typing/clearing. Ignore "reset"
                            // events that fire when the `value` prop changes
                            // programmatically (e.g. when selectedShapes updates
                            // during a marquee selection). Without this guard,
                            // transient single-shape selection states would push
                            // that shape's size into `updateShapesProps` and
                            // standardize every newly-selected shape's size.
                            if (reason !== "input" && reason !== "clear") return;
                            const regex = /^\d{0,4}(\.\d{0,1})?$/;
                            if (regex.test(newInputValue) || newInputValue === "") {
                              setDisplayFontSize(newInputValue);
                              if (!newInputValue.endsWith(".") && newInputValue !== "") {
                                const parsedValue = parseFloat(newInputValue);
                                const currentFontScale =
                                  fontScale && fontScale !== "Mixed" && !isNaN(Number(fontScale))
                                    ? Number(fontScale)
                                    : 1;
                                if (!isNaN(parsedValue) && currentFontScale > 0) {
                                  const scaledValue = parsedValue / currentFontScale;
                                  setFontSize(scaledValue);
                                  updateShapesProps({ size: scaledValue });
                                }
                              }
                            }
                          }}
                          onBlur={() => {
                            if (displayFontSize === "" || displayFontSize === "Mixed") {
                              const defaultSize = 10;
                              setDisplayFontSize(defaultSize.toString());
                              setFontSize(defaultSize);
                              updateShapesProps({ size: defaultSize });
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              placeholder={displayFontSize === "Mixed" ? "Mixed" : ""}
                              inputProps={{
                                ...params.inputProps,
                                style: {
                                  fontSize: "12px",
                                  paddingLeft: "8px",
                                  paddingRight: "8px",
                                  color: "#2E3860",
                                  textAlign: "left",
                                },
                              }}
                              classes={{
                                root: classes.inputRoot,
                              }}
                            />
                          )}
                        />

                        {/* External icon to open the font size dropdown */}
                        <DropdownChevronIcon
                          onClick={() => {
                            setFontSizeOpen(!fontSizeOpen);
                            if (!fontSizeOpen) {
                              setFontSelectorOpen(false); // close font selector when font size is opened
                            }
                          }}
                          style={{
                            position: "absolute",
                            cursor: "pointer",
                            pointerEvents: "auto",
                            zIndex: 10,
                          }}
                        />

                        <span
                          style={{
                            position: "absolute",
                            left:
                              displayFontSize && displayFontSize !== "Mixed"
                                ? `${8 + displayFontSize.length * 6 + 4}px`
                                : "8px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            pointerEvents: "none",
                            fontSize: "12px",
                            opacity: displayFontSize && displayFontSize !== "Mixed" ? 1 : 0,
                            color: "#2E3860",
                          }}
                        >
                          px
                        </span>
                      </div>
                    </Tooltip>
                  </div>
                </div>
                {/* Line Height y Letter Spacing */}
                <div className="flex gap-2">
                  <div className="relative" style={{ flex: 1 }}>
                    <Tooltip
                      title="Line height"
                      arrow
                      placement="top"
                      classes={{ tooltip: classesTooltip.tooltip, arrow: classesTooltip.arrow }}
                    >
                      <div style={{ position: "relative", width: "100%" }}>
                        <div
                          style={{
                            position: "absolute",
                            left: "8px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            pointerEvents: "auto",
                            display: "flex",
                            alignItems: "center",
                            color: "#2E3860",
                            cursor: "ew-resize",
                          }}
                          onMouseDown={lineHeightDrag.onMouseDown}
                          title="Drag to adjust line height"
                        >
                          <LineHeightIcon style={{ width: "16px", height: "16px" }} />
                        </div>
                        {(() => {
                          // lineHeight is stored as an em multiplier. Display it as the
                          // px equivalent (multiplier × font size) and convert back on edit
                          // so downstream consumers keep receiving the em value unchanged.
                          const fontSizePx =
                            displayFontSize && displayFontSize !== "Mixed" ? parseFloat(displayFontSize) : NaN;
                          const lineHeightEm =
                            selectedShape?.props?.lineHeight !== undefined ? selectedShape.props.lineHeight : 1;
                          const lineHeightPxDisplay =
                            !isNaN(fontSizePx) && fontSizePx > 0 ? Math.round(lineHeightEm * fontSizePx * 10) / 10 : "";
                          return (
                            <>
                              <input
                                type="number"
                                style={{
                                  border: "1px solid #E1E6EB",
                                  paddingLeft: "32px",
                                  paddingRight: "8px",
                                  color: "#2E3860",
                                  textAlign: "left",
                                  fontSize: "12px",
                                }}
                                value={lineHeightPxDisplay}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  const regex = /^\d{0,4}(\.\d{0,1})?$/;
                                  if (!regex.test(value)) return;
                                  const parsedPx = parseFloat(value);
                                  if (isNaN(parsedPx) || isNaN(fontSizePx) || fontSizePx <= 0) return;
                                  const em = parsedPx / fontSizePx;
                                  updateShapesProps({ lineHeight: em });
                                }}
                                className="w-full h-[36px] text-[10px] rounded-[8px] no-arrows text-end"
                                step="1"
                                min="0"
                                max="500"
                              />
                              <span
                                style={{
                                  position: "absolute",
                                  left: `${32 + String(lineHeightPxDisplay).length * 6 + 4}px`,
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                  pointerEvents: "none",
                                  fontSize: "12px",
                                  opacity: lineHeightPxDisplay !== "" ? 1 : 0,
                                  color: "#2E3860",
                                }}
                              >
                                px
                              </span>
                            </>
                          );
                        })()}
                      </div>
                    </Tooltip>
                  </div>

                  <div className="relative" style={{ flex: 1 }}>
                    <Tooltip
                      title="Letter spacing"
                      arrow
                      placement="top"
                      classes={{ tooltip: classesTooltip.tooltip, arrow: classesTooltip.arrow }}
                    >
                      <div style={{ position: "relative", width: "100%" }}>
                        <div
                          style={{
                            position: "absolute",
                            left: "8px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            pointerEvents: "auto",
                            display: "flex",
                            alignItems: "center",
                            color: "#2E3860",
                            cursor: "ew-resize",
                          }}
                          onMouseDown={letterSpacingDrag.onMouseDown}
                          title="Drag to adjust letter spacing"
                        >
                          <LetterSpaceIcon style={{ width: "16px", height: "16px" }} />
                        </div>
                        <input
                          type="number"
                          style={{
                            border: "1px solid #E1E6EB",
                            paddingLeft: "32px",
                            paddingRight: "8px",
                            textAlign: "left",
                            fontSize: "12px",
                          }}
                          value={
                            selectedShape?.props?.letterSpacing !== undefined ? selectedShape.props.letterSpacing : 0
                          }
                          onChange={(e) => {
                            const value = e.target.value;
                            const regex = /^-?\d{0,4}(\.\d{0,1})?$/;
                            if (regex.test(value)) {
                              const parsedValue = parseFloat(value);
                              if (!isNaN(parsedValue)) {
                                updateShapesProps({ letterSpacing: parsedValue });
                              }
                            }
                          }}
                          className="w-full h-[36px] text-[10px] rounded-[8px] no-arrows text-end"
                          step="0.1"
                          min="-7.5"
                          max="50"
                        />
                        <span
                          style={{
                            position: "absolute",
                            left:
                              selectedShape?.props?.letterSpacing !== undefined
                                ? `${32 + String(selectedShape.props.letterSpacing).length * 6 + 4}px`
                                : "32px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            pointerEvents: "none",
                            fontSize: "12px",
                            opacity: selectedShape?.props?.letterSpacing !== undefined ? 1 : 0,
                            color: "#2E3860",
                          }}
                        >
                          px
                        </span>
                      </div>
                    </Tooltip>
                  </div>
                </div>
              </div>
            </>

            <div className="mt-2">
              {/* First row: TextBoxComponent occupying the entire width */}
              <div className="mb-2">
                <TextBoxComponent selectedShape={selectedShape} editor={editor} selectedShapes={selectedShapes} />
              </div>

              {/* Second row: Horizontal Alignment (left) and Vertical Alignment (right) */}
              <div className="flex items-center justify-between gap-2">
                {/* Horizontal Alignment */}
                <div
                  className="flex items-center"
                  style={{
                    border: "1px solid #E4E4EA",
                    borderRadius: "4px",
                  }}
                >
                  {textAlignment.map((alignment, index) => (
                    <Tooltip
                      key={alignment.value}
                      title={alignment.label}
                      arrow
                      classes={{ tooltip: classesTooltip.tooltip, arrow: classesTooltip.arrow }}
                    >
                      <div
                        onClick={() => handleAlignmentChange(alignment.value)}
                        style={{
                          padding: "8px",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRight: index < textAlignment.length - 1 ? "1px solid #E4E4EA" : "none",
                          backgroundColor:
                            selectedShape && selectedShape.props.textAlign === alignment.value
                              ? "#E2E2FC"
                              : "transparent",
                        }}
                        className="flex max-w-[38px] max-h-[38px] hover:!bg-[#E2E2FC] transition-colors"
                      >
                        <div
                          style={{
                            width: "16px",
                            height: "16px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {alignment.icon}
                        </div>
                      </div>
                    </Tooltip>
                  ))}
                  {/* <Tooltip
                    title="Justify"
                    arrow
                    classes={{ tooltip: classesTooltip.tooltip, arrow: classesTooltip.arrow }}
                  >
                    <div
                      onClick={() => toggleProp("textJustify", "text-justify", "start")}
                      style={{
                        padding: "8px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor:
                          selectedShape && selectedShape.props.textJustify === "text-justify"
                            ? "#E2E2FC"
                            : "transparent",
                      }}
                      className="flex max-w-[38px] max-h-[38px] hover:bg-[#E2E2FC] transition-colors"
                    >
                      <div className="scale-[1.35]">
                        <RowsIcon />
                      </div>
                    </div>
                  </Tooltip> */}
                </div>

                {/* Vertical Alignment - Always visible */}
                <div
                  className="flex items-center"
                  style={{
                    border: "1px solid #E4E4EA",
                    borderRadius: "4px",
                  }}
                >
                  {verticalAlignment.map((alignment, index) => (
                    <Tooltip
                      key={alignment.value}
                      title={alignment.label}
                      arrow
                      classes={{ tooltip: classesTooltip.tooltip, arrow: classesTooltip.arrow }}
                    >
                      <div
                        onClick={() => handleVerticalAlignmentChange(alignment.value)}
                        style={{
                          padding: "8px",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRight: index < verticalAlignment.length - 1 ? "1px solid #E4E4EA" : "none",
                          backgroundColor:
                            selectedShape && selectedShape.props.verticalAlign === alignment.value
                              ? "#E2E2FC"
                              : "transparent",
                        }}
                        className="flex max-w-[38px] max-h-[38px] hover:!bg-[#E2E2FC] transition-colors"
                      >
                        <div
                          style={{
                            width: "16px",
                            height: "16px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {alignment.icon}
                        </div>
                      </div>
                    </Tooltip>
                  ))}
                </div>
              </div>

              {/* Brand Font Presets dropdown — hidden, brand fonts are now shown
                 in the main font dropdown's "Brand Fonts" section instead.
              {fontPresets.length > 0 && (
                ...
              )}
              */}
            </div>
          </div>
        </>
      )}
      {/* Brand Fonts queue dialog — opened after a font upload. Lists every
          detected family; each row can be added to the brand kit (inline font
          name input → Save) or skipped. Rows disappear from the queue when
          added or skipped; the dialog auto-closes when empty. */}
      <BrandFontsQueueDialog
        open={brandFontQueueOpen}
        families={brandFontQueue}
        onClose={() => {
          setBrandFontQueueOpen(false);
          setBrandFontQueue([]);
        }}
        onSave={async (input) => {
          await addFontPreset(input);
          // Right sidebar flow: once a preset is added from here, surface
          // the Brand Kit panel in the left sidebar so the user lands on
          // their updated brand fonts.
          window.dispatchEvent(new CustomEvent("open-brand-kit-fonts", { detail: {} }));
        }}
        onCancel={async (remaining) => {
          // The user clicked the footer "Cancel" — delete every
          // already-uploaded font in the remaining queue from both the
          // font CDN / local registry AND the Brand Kit font assets, so
          // the cancellation is a true rollback of the upload.
          const allVariants = remaining.flatMap((f) => f.variantNames);
          for (const fontName of allVariants) {
            try {
              await s3CustomFontService.removeFontFromRegistry(fontName);
            } catch (err) {
              console.warn(`Failed to remove ${fontName} from CDN registry:`, err);
            }
            try {
              await customFontStorage.deleteCustomFont(fontName);
            } catch (err) {
              console.warn(`Failed to remove ${fontName} from local storage:`, err);
            }
            const loaded = Array.from(document.fonts).find((f) => f.family.toLowerCase() === fontName.toLowerCase());
            if (loaded) document.fonts.delete(loaded);
          }
          // Delete matching Brand Kit font assets by name.
          const fontsItems = useBrandKitStore.getState().fonts.items as Array<{ id: string; name: string }>;
          const lowerVariants = new Set(allVariants.map((n) => n.toLowerCase()));
          const toRemove = fontsItems.filter((item) => lowerVariants.has((item.name || "").toLowerCase()));
          for (const item of toRemove) {
            try {
              await useBrandKitStore.getState().removeAsset("font", item.id);
            } catch (err) {
              console.warn(`Failed to remove Brand Kit font asset ${item.name}:`, err);
            }
          }
          s3CustomFontService.clearCache();
          await loadBrandKit();
          window.dispatchEvent(new Event("custom-fonts-updated"));
        }}
      />
    </div>
  );
};

export default TextParameters;
